import React, { useCallback, useEffect, useMemo, useState } from 'react';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import * as XLSX from 'xlsx';
import {
  AlertTriangle,
  Car,
  ClipboardList,
  Download,
  FileText,
  Pencil,
  RefreshCw,
  Save,
  ShieldCheck,
  Trash2,
  Users,
  X,
} from 'lucide-react';
import { api, setCurrentPageContext } from '../../services/api';
import senerfiFormatoLogoUrl from '../../assets/images/Senerfi-formato.png';
import recordOperationsDownload from '../../utils/recordOperationsDownload';

const PROGRAM_OPTIONS = [
  'Fortalecimiento de la gestión institucional',
  'Comportamiento humano',
  'Vehículos seguros',
  'Infraestructura segura',
  'Atención a víctimas',
];

const VEHICLE_DOCUMENT_TYPES = [
  'SOAT',
  'RTM',
  'Poliza',
  'Ficha tecnica',
  'Prueba dielectrica',
  'Inspeccion de izaje',
  'Certificado de izaje',
  'Otro',
];

const PROGRAM_STATUS_OPTIONS = ['Programado', 'En ejecucion', 'Completado', 'Vencido', 'Cancelado'];
const REPORT_STATUS_OPTIONS = ['Pendiente', 'En proceso', 'Cerrado'];
const INFRACTION_STATUS_OPTIONS = ['Pendiente', 'En proceso', 'Pagada', 'Apelada'];

const PESV_INDICATORS = [
  { code: 'TSV', name: 'Tasa de siniestros viales', periodicity: 'Trimestral', unit: 'Porcentaje' },
  { code: 'RSVI', name: 'Riesgos de seguridad vial identificados', periodicity: 'Anual', unit: 'Numerico' },
  { code: 'GRV', name: 'Gestion de riesgos viales', periodicity: 'Anual', unit: 'Numerico' },
  { code: 'CM', name: 'Cumplimiento metas PESV', periodicity: 'Trimestral', unit: 'Porcentaje' },
  { code: 'CPLAN PESV', name: 'Cumplimiento del plan anual PESV', periodicity: 'Trimestral', unit: 'Porcentaje' },
  { code: 'EJLC', name: 'Cumplimiento de la jornada laboral en conductores', periodicity: 'Mensual', unit: 'Porcentaje' },
  { code: 'IDP', name: 'Inspecciones diarias preoperacionales', periodicity: 'Mensual', unit: 'Porcentaje' },
  { code: 'CPMVH', name: 'Cumplimiento plan mantenimiento preventivo de vehículos', periodicity: 'Trimestral', unit: 'Porcentaje' },
  { code: 'CPF PESV', name: 'Cumplimiento del plan de formación en seguridad vial', periodicity: 'Trimestral', unit: 'Porcentaje' },
  { code: 'CPF SV', name: 'Cobertura del plan de formación en seguridad vial', periodicity: 'Trimestral', unit: 'Porcentaje' },
  { code: 'NCAC', name: 'No conformidades de auditoria cerradas', periodicity: 'Trimestral', unit: 'Porcentaje' },
];

const createProgramForm = () => ({
  id: '',
  program_name: PROGRAM_OPTIONS[0],
  activity_name: '',
  activity_date: '',
  due_date: '',
  status: 'Programado',
  responsible_name: '',
  notes: '',
  files: [],
  existingAttachments: [],
});

const createIndicatorForm = () => ({
  id: '',
  indicator_code: PESV_INDICATORS[0].code,
  indicator_name: PESV_INDICATORS[0].name,
  measurement_date: '',
  period_year: new Date().getFullYear().toString(),
  period_label: '',
  result_value: '',
  target_value: '',
  responsible_name: '',
  variables_text: '',
  analysis: '',
  files: [],
  existingAttachments: [],
});

const createPlanForm = () => ({
  id: '',
  title: 'Plan Estratégico de Seguridad Vial',
  activity_date: '',
  responsible_name: '',
  notes: '',
  files: [],
  existingAttachments: [],
});

const createVehicleDocForm = () => ({
  id: '',
  related_vehicle_id: '',
  document_type: VEHICLE_DOCUMENT_TYPES[0],
  due_date: '',
  status: 'Vigente',
  notes: '',
  files: [],
  existingAttachments: [],
});

const createInfractionForm = () => ({
  id: '',
  related_driver_id: '',
  related_vehicle_id: '',
  infraction_name: '',
  infraction_code: '',
  activity_date: '',
  status: 'Pendiente',
  authority: '',
  amount: '',
  location: '',
  ticket_number: '',
  notes: '',
  files: [],
  existingAttachments: [],
});

const createAutogestionForm = () => ({
  id: '',
  title: 'Reporte de autogestion PESV',
  activity_date: '',
  period_year: new Date().getFullYear().toString(),
  period_label: '',
  responsible_name: '',
  status: 'Pendiente',
  notes: '',
  files: [],
  existingAttachments: [],
});

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

function fmtDate(value) {
  const raw = String(value || '').trim();
  if (!raw) return '-';
  if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
  return `${raw.slice(8, 10)}/${raw.slice(5, 7)}/${raw.slice(0, 4)}`;
}

function fmtMoney(value) {
  const num = Number(value || 0);
  if (!Number.isFinite(num) || num <= 0) return '-';
  try {
    return num.toLocaleString('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 });
  } catch (_) {
    return `$ ${Math.round(num)}`;
  }
}

function normalizeSearch(value) {
  return String(value || '').toLowerCase().trim();
}

function includesSearch(values, term) {
  const search = normalizeSearch(term);
  if (!search) return true;
  return values.some((value) => normalizeSearch(value).includes(search));
}

function inDateRange(value, from, to) {
  const date = String(value || '').trim();
  if (!from && !to) return true;
  if (!date) return false;
  if (from && date < from) return false;
  if (to && date > to) return false;
  return true;
}

function withEditData(base, item) {
  return {
    ...base,
    id: String(item?.id || ''),
    files: [],
    existingAttachments: asArray(item?.attachments),
  };
}

function ActionButtons({ onEdit, onDelete }) {
  return (
    <div className="flex items-center justify-center gap-2">
      <button type="button" onClick={onEdit} className="rounded-full p-2 text-blue-600 hover:bg-blue-50" title="Editar registro">
        <Pencil className="h-4 w-4" />
      </button>
      <button type="button" onClick={onDelete} className="rounded-full p-2 text-red-600 hover:bg-red-50" title="Eliminar registro">
        <Trash2 className="h-4 w-4" />
      </button>
    </div>
  );
}

function FiltersCard({ children }) {
  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
      <div className="mb-3 text-sm font-semibold text-gray-800">Filtros</div>
      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">{children}</div>
    </div>
  );
}

function sheetFileName(prefix) {
  const stamp = new Date().toISOString().slice(0, 10);
  return `${prefix}_${stamp}.xlsx`;
}

function attachmentNames(value) {
  return asArray(value).map((item) => item?.name || '').filter(Boolean).join(' | ');
}

function escapeHtml(value) {
  return String(value || '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
}

function pdfFileName(prefix) {
  const stamp = new Date().toISOString().slice(0, 10);
  return `${prefix}_${stamp}.pdf`;
}

function resolveAppAssetUrl(assetPath) {
  const value = String(assetPath || '').trim();
  if (!value) return '';
  if (/^(https?:|data:|blob:)/i.test(value)) return value;
  try {
    return new URL(value, window.location.origin).toString();
  } catch (_) {
    return value;
  }
}

function buildPdfTable(rows, columns) {
  const safeRows = asArray(rows);
  if (!safeRows.length) {
    return '<div style="padding:14px 16px;border:1px solid #e5e7eb;border-radius:14px;background:#f9fafb;color:#6b7280;font-size:12px;">Sin datos para esta seccion.</div>';
  }

  const head = columns.map((column) => (
    `<th style="padding:9px 10px;border-bottom:1px solid #e5e7eb;background:#f9fafb;color:#4b5563;text-align:left;font-size:11px;">${escapeHtml(column.label)}</th>`
  )).join('');

  const body = safeRows.map((row) => (
    `<tr>${columns.map((column) => `<td style="padding:9px 10px;border-bottom:1px solid #f3f4f6;vertical-align:top;font-size:11px;color:#111827;">${escapeHtml(row?.[column.key] ?? '-')}</td>`).join('')}</tr>`
  )).join('');

  return `
    <div style="border:1px solid #e5e7eb;border-radius:16px;overflow:hidden;">
      <table style="width:100%;border-collapse:collapse;">
        <thead><tr>${head}</tr></thead>
        <tbody>${body}</tbody>
      </table>
    </div>
  `;
}

function parseDateValue(value) {
  const raw = String(value || '').trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) return null;
  const parsed = new Date(`${raw}T00:00:00`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function diffDaysFromToday(value) {
  const target = parseDateValue(value);
  if (!target) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((target.getTime() - today.getTime()) / 86400000);
}

function getAlertMeta(days) {
  if (days == null) return null;
  if (days < 0) return { level: 'expired', label: 'Vencido', order: 0 };
  if (days <= 7) return { level: 'critical', label: 'Por vencer', order: 1 };
  if (days <= 30) return { level: 'warning', label: 'Proximo', order: 2 };
  return null;
}

function alertClasses(level) {
  if (level === 'expired') return 'border-red-200 bg-red-50 text-red-700';
  if (level === 'critical') return 'border-yellow-200 bg-yellow-50 text-yellow-700';
  return 'border-orange-200 bg-orange-50 text-orange-700';
}

function alertTextFromDays(days) {
  if (days == null) return 'Sin fecha';
  if (days < 0) return `Vencido hace ${Math.abs(days)} dia(s)`;
  if (days === 0) return 'Vence hoy';
  return `Vence en ${days} dia(s)`;
}

function getVehicleDocumentVisualState(item) {
  const manualStatus = normalizeSearch(item?.status);
  if (manualStatus === 'vencido') {
    return {
      rowClass: 'bg-red-50/70',
      badgeClass: 'border-red-200 bg-red-100 text-red-700',
      label: 'Vencido',
      helper: 'Marcado manualmente como vencido',
    };
  }

  const days = diffDaysFromToday(item?.due_date);
  const meta = getAlertMeta(days);
  if (meta?.level === 'expired') {
    return {
      rowClass: 'bg-red-50/70',
      badgeClass: 'border-red-200 bg-red-100 text-red-700',
      label: 'Vencido',
      helper: alertTextFromDays(days),
    };
  }
  if (meta?.level === 'critical') {
    return {
      rowClass: 'bg-yellow-50/70',
      badgeClass: 'border-yellow-200 bg-yellow-100 text-yellow-700',
      label: 'Por vencer',
      helper: alertTextFromDays(days),
    };
  }
  if (meta?.level === 'warning') {
    return {
      rowClass: 'bg-orange-50/70',
      badgeClass: 'border-orange-200 bg-orange-100 text-orange-700',
      label: 'Proximo',
      helper: alertTextFromDays(days),
    };
  }

  return {
    rowClass: '',
    badgeClass: 'border-emerald-200 bg-emerald-100 text-emerald-700',
    label: 'Vigente',
    helper: item?.due_date ? alertTextFromDays(days) : 'Sin fecha de vencimiento',
  };
}

function getMaintenanceHistoryVisualState(item) {
  const startDate = String(item?.start_date || item?.date || '').trim();
  const endDate = String(item?.end_date || '').trim();
  const startDays = diffDaysFromToday(startDate);
  const endDays = diffDaysFromToday(endDate);

  if (startDays != null && startDays > 0) {
    if (startDays <= 7) {
      return {
        rowClass: 'bg-yellow-50/70',
        badgeClass: 'border-yellow-200 bg-yellow-100 text-yellow-700',
        label: 'Proximo',
        helper: `Programado para ${fmtDate(startDate)} · ${alertTextFromDays(startDays)}`,
      };
    }
    if (startDays <= 30) {
      return {
        rowClass: 'bg-orange-50/70',
        badgeClass: 'border-orange-200 bg-orange-100 text-orange-700',
        label: 'Programado',
        helper: `Programado para ${fmtDate(startDate)} · ${alertTextFromDays(startDays)}`,
      };
    }
    return {
      rowClass: '',
      badgeClass: 'border-blue-200 bg-blue-100 text-blue-700',
      label: 'Programado',
      helper: `Programado para ${fmtDate(startDate)}`,
    };
  }

  if (endDate) {
    if (endDays != null && endDays >= 0) {
      const accent = endDays <= 7 ? 'yellow' : 'orange';
      return {
        rowClass: accent === 'yellow' ? 'bg-yellow-50/70' : 'bg-orange-50/70',
        badgeClass: accent === 'yellow' ? 'border-yellow-200 bg-yellow-100 text-yellow-700' : 'border-orange-200 bg-orange-100 text-orange-700',
        label: 'En curso',
        helper: `Cierre previsto ${fmtDate(endDate)} · ${alertTextFromDays(endDays)}`,
      };
    }
    return {
      rowClass: '',
      badgeClass: 'border-emerald-200 bg-emerald-100 text-emerald-700',
      label: 'Ejecutado',
      helper: `Cerrado el ${fmtDate(endDate)}`,
    };
  }

  if (startDays != null && startDays <= -30) {
    return {
      rowClass: 'bg-red-50/70',
      badgeClass: 'border-red-200 bg-red-100 text-red-700',
      label: 'Atrasado',
      helper: `Iniciado el ${fmtDate(startDate)} sin fecha de cierre`,
    };
  }

  if (startDays != null && startDays <= 0) {
    return {
      rowClass: 'bg-yellow-50/70',
      badgeClass: 'border-yellow-200 bg-yellow-100 text-yellow-700',
      label: 'En curso',
      helper: `Iniciado el ${fmtDate(startDate)}`,
    };
  }

  return {
    rowClass: '',
    badgeClass: 'border-gray-200 bg-gray-100 text-gray-700',
    label: 'Historico',
    helper: startDate ? `Registro del ${fmtDate(startDate)}` : 'Sin fecha de programacion',
  };
}

function maintenanceSupportItems(item) {
  return asArray(item?.support_files).length
    ? asArray(item.support_files).map((url) => ({ name: String(url).split('/').pop(), url }))
    : (item?.support_file ? [{ name: String(item.support_file).split('/').pop(), url: item.support_file }] : []);
}

function FileLinks({ attachments }) {
  const items = asArray(attachments);
  if (items.length === 0) return <span className="text-xs text-gray-400">Sin archivos</span>;
  return (
    <div className="flex flex-wrap gap-2">
      {items.map((file, index) => (
        <a
          key={`${file?.url || file?.name || 'file'}-${index}`}
          href={file?.url || '#'}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 rounded-full border border-blue-200 bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700 hover:bg-blue-100"
        >
          <Download className="h-3.5 w-3.5" />
          {file?.name || `Archivo ${index + 1}`}
        </a>
      ))}
    </div>
  );
}

function TabButton({ active, icon: Icon, label, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm ${
        active
          ? 'border-orange-200 bg-orange-50 font-semibold text-orange-700'
          : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
      }`}
    >
      <Icon className="h-4 w-4" />
      {label}
    </button>
  );
}

export default function PesvModule({ location } = {}) {
  const [activeTab, setActiveTab] = useState('programas');
  // Reporta a Administración TI > Auditoría qué pestaña de PESV (submódulo
  // de Transportes) está viendo el usuario (ver X-Senerfi-Page en
  // src/services/api.js).
  useEffect(() => {
    setCurrentPageContext(`/sede/${location || ''}/transportes/pesv/${activeTab || ''}`);
  }, [location, activeTab]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  // Vehículos inactivos (fuera de la empresa): no generan alertas PESV.
  const [inactiveVehicleIds, setInactiveVehicleIds] = useState(() => new Set());
  const [drivers, setDrivers] = useState([]);
  const [infractionCatalog, setInfractionCatalog] = useState([]);
  const [records, setRecords] = useState({
    program_activity: [],
    indicator_record: [],
    plan_document: [],
    vehicle_document: [],
    infraction_record: [],
    autogestion_report: [],
  });
  const [vehicleHistory, setVehicleHistory] = useState([]);
  const [loadingVehicleHistory, setLoadingVehicleHistory] = useState(false);

  const [programForm, setProgramForm] = useState(createProgramForm());
  const [indicatorForm, setIndicatorForm] = useState(createIndicatorForm());
  const [planForm, setPlanForm] = useState(createPlanForm());
  const [vehicleDocForm, setVehicleDocForm] = useState(createVehicleDocForm());
  const [infractionForm, setInfractionForm] = useState(createInfractionForm());
  const [autogestionForm, setAutogestionForm] = useState(createAutogestionForm());
  const [infractionSearch, setInfractionSearch] = useState('');
  const [programFilters, setProgramFilters] = useState({ text: '', from: '', to: '' });
  const [indicatorFilters, setIndicatorFilters] = useState({ text: '', from: '', to: '' });
  const [planFilters, setPlanFilters] = useState({ text: '', from: '', to: '' });
  const [vehicleDocFilters, setVehicleDocFilters] = useState({ vehicleId: '', text: '', from: '', to: '' });
  const [vehicleHistoryFilters, setVehicleHistoryFilters] = useState({ type: '', state: '', from: '', to: '' });
  const [infractionFilters, setInfractionFilters] = useState({ vehicleId: '', driverId: '', text: '', from: '', to: '' });
  const [autogestionFilters, setAutogestionFilters] = useState({ text: '', from: '', to: '' });

  const vehiclesById = useMemo(() => {
    const map = new Map();
    asArray(vehicles).forEach((item) => map.set(String(item.id), item));
    return map;
  }, [vehicles]);

  const driversById = useMemo(() => {
    const map = new Map();
    asArray(drivers).forEach((item) => map.set(String(item.id), item));
    return map;
  }, [drivers]);

  const selectedVehicleForSheet = useMemo(
    () => vehiclesById.get(String(vehicleDocForm.related_vehicle_id || '')) || null,
    [vehicleDocForm.related_vehicle_id, vehiclesById],
  );

  const selectedIndicatorMeta = useMemo(
    () => PESV_INDICATORS.find((item) => item.code === indicatorForm.indicator_code) || PESV_INDICATORS[0],
    [indicatorForm.indicator_code],
  );

  const filteredInfractionCatalog = useMemo(
    () => asArray(infractionCatalog).filter((item) => includesSearch([item?.code, item?.name], infractionSearch)),
    [infractionCatalog, infractionSearch],
  );

  const loadBaseData = useCallback(async () => {
    const [vehiclesRes, driversRes, infractionRes] = await Promise.all([
      api.vehicles.get(),
      api.conductores.get(),
      api.pesv.catalog({ type: 'infraction' }),
    ]);
    setVehicles(asArray(vehiclesRes?.data));
    setInactiveVehicleIds(new Set(asArray(vehiclesRes?.data)
      .filter((v) => String(v?.status || 'Activo').toLowerCase() === 'inactivo')
      .map((v) => String(v.id))));
    setDrivers(asArray(driversRes?.data));
    setInfractionCatalog(asArray(infractionRes?.data));
  }, []);

  const loadRecords = useCallback(async () => {
    const recordTypes = [
      'program_activity',
      'indicator_record',
      'plan_document',
      'vehicle_document',
      'infraction_record',
      'autogestion_report',
    ];
    const results = await Promise.all(recordTypes.map((recordType) => api.pesv.list({ record_type: recordType })));
    const next = {};
    recordTypes.forEach((recordType, index) => {
      next[recordType] = asArray(results[index]?.data);
    });
    setRecords(next);
  }, []);

  const loadEverything = useCallback(async () => {
    setLoading(true);
    try {
      await Promise.all([loadBaseData(), loadRecords()]);
    } catch (_) {
      setMessage({ type: 'error', text: 'No fue posible cargar la información del módulo PESV.' });
    } finally {
      setLoading(false);
    }
  }, [loadBaseData, loadRecords]);

  useEffect(() => {
    loadEverything();
  }, [loadEverything]);

  useEffect(() => {
    const loadHistory = async () => {
      if (!vehicleDocForm.related_vehicle_id) {
        setVehicleHistory([]);
        return;
      }
      setLoadingVehicleHistory(true);
      try {
        const res = await api.maintenances.history(Number(vehicleDocForm.related_vehicle_id));
        setVehicleHistory(asArray(res?.data));
      } catch (_) {
        setVehicleHistory([]);
      } finally {
        setLoadingVehicleHistory(false);
      }
    };
    loadHistory();
  }, [vehicleDocForm.related_vehicle_id]);

  const resetMessage = () => setMessage(null);

  const appendFiles = (fd, files) => {
    asArray(files).forEach((file) => {
      if (file instanceof File) fd.append('files[]', file, file.name);
    });
  };

  const appendExistingAttachments = (fd, attachments) => {
    asArray(attachments).forEach((file) => {
      const path = String(file?.path || file || '').trim();
      if (path) fd.append('existing_attachments[]', path);
    });
  };

  const saveRecord = async (builder, resetForm) => {
    resetMessage();
    try {
      const fd = builder();
      await api.pesv.upsert(fd);
      setMessage({ type: 'success', text: 'Registro PESV guardado correctamente.' });
      resetForm();
      await loadRecords();
    } catch (error) {
      setMessage({ type: 'error', text: error?.message || 'No fue posible guardar el registro del PESV.' });
    }
  };

  const handleEditProgram = (item) => {
    setActiveTab('programas');
    setProgramForm(withEditData(createProgramForm(), {
      ...item,
      program_name: item?.category || PROGRAM_OPTIONS[0],
      activity_name: item?.title || '',
    }));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleEditIndicator = (item) => {
    setActiveTab('indicadores');
    setIndicatorForm(withEditData(createIndicatorForm(), {
      ...item,
      indicator_code: item?.code || PESV_INDICATORS[0].code,
      indicator_name: item?.category || PESV_INDICATORS[0].name,
      measurement_date: item?.activity_date || '',
      result_value: item?.metric_value ?? '',
      target_value: item?.meta_value ?? '',
      analysis: item?.notes || '',
      variables_text: item?.payload?.variables_text || '',
      period_year: item?.period_year || new Date().getFullYear().toString(),
      period_label: item?.period_label || '',
      responsible_name: item?.responsible_name || '',
    }));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleEditPlan = (item) => {
    setActiveTab('plan');
    setPlanForm(withEditData(createPlanForm(), {
      ...item,
      title: item?.title || 'Plan Estratégico de Seguridad Vial',
      activity_date: item?.activity_date || '',
      responsible_name: item?.responsible_name || '',
      notes: item?.notes || '',
    }));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleEditVehicleDocument = (item) => {
    setActiveTab('vehiculos');
    setVehicleDocForm(withEditData(createVehicleDocForm(), {
      ...item,
      related_vehicle_id: item?.related_vehicle_id ? String(item.related_vehicle_id) : '',
      document_type: item?.category || VEHICLE_DOCUMENT_TYPES[0],
      due_date: item?.due_date || '',
      status: item?.status || 'Vigente',
      notes: item?.notes || '',
    }));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleEditInfraction = (item) => {
    setActiveTab('infracciones');
    setInfractionForm(withEditData(createInfractionForm(), {
      ...item,
      related_driver_id: item?.related_driver_id ? String(item.related_driver_id) : '',
      related_vehicle_id: item?.related_vehicle_id ? String(item.related_vehicle_id) : '',
      infraction_name: item?.category || '',
      infraction_code: item?.code || '',
      activity_date: item?.activity_date || '',
      status: item?.status || 'Pendiente',
      authority: item?.payload?.authority || '',
      amount: item?.payload?.amount ?? '',
      location: item?.payload?.location || '',
      ticket_number: item?.payload?.ticket_number || '',
      notes: item?.notes || '',
    }));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleEditAutogestion = (item) => {
    setActiveTab('autogestion');
    setAutogestionForm(withEditData(createAutogestionForm(), {
      ...item,
      title: item?.title || 'Reporte de autogestion PESV',
      activity_date: item?.activity_date || '',
      period_year: item?.period_year || new Date().getFullYear().toString(),
      period_label: item?.period_label || '',
      responsible_name: item?.responsible_name || '',
      status: item?.status || 'Pendiente',
      notes: item?.notes || '',
    }));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSaveProgram = async (e) => {
    e.preventDefault();
    await saveRecord(() => {
      const fd = new FormData();
      if (programForm.id) fd.append('id', programForm.id);
      fd.append('record_type', 'program_activity');
      fd.append('category', programForm.program_name);
      fd.append('title', programForm.activity_name);
      fd.append('activity_date', programForm.activity_date);
      fd.append('due_date', programForm.due_date);
      fd.append('status', programForm.status);
      fd.append('responsible_name', programForm.responsible_name);
      fd.append('notes', programForm.notes);
      appendExistingAttachments(fd, programForm.existingAttachments);
      appendFiles(fd, programForm.files);
      return fd;
    }, () => setProgramForm(createProgramForm()));
  };

  const handleIndicatorCodeChange = (code) => {
    const meta = PESV_INDICATORS.find((item) => item.code === code) || PESV_INDICATORS[0];
    setIndicatorForm((prev) => ({
      ...prev,
      indicator_code: meta.code,
      indicator_name: meta.name,
    }));
  };

  const handleSaveIndicator = async (e) => {
    e.preventDefault();
    await saveRecord(() => {
      const fd = new FormData();
      if (indicatorForm.id) fd.append('id', indicatorForm.id);
      fd.append('record_type', 'indicator_record');
      fd.append('category', indicatorForm.indicator_name);
      fd.append('title', `${indicatorForm.indicator_code} - ${indicatorForm.indicator_name}`);
      fd.append('code', indicatorForm.indicator_code);
      fd.append('period_year', indicatorForm.period_year);
      fd.append('period_label', indicatorForm.period_label);
      fd.append('activity_date', indicatorForm.measurement_date);
      fd.append('responsible_name', indicatorForm.responsible_name);
      fd.append('metric_value', indicatorForm.result_value);
      fd.append('meta_value', indicatorForm.target_value);
      fd.append('notes', indicatorForm.analysis);
      fd.append(
        'payload_json',
        JSON.stringify({
          periodicity: selectedIndicatorMeta.periodicity,
          unit: selectedIndicatorMeta.unit,
          variables_text: indicatorForm.variables_text,
        }),
      );
      appendExistingAttachments(fd, indicatorForm.existingAttachments);
      appendFiles(fd, indicatorForm.files);
      return fd;
    }, () => setIndicatorForm(createIndicatorForm()));
  };

  const handleSavePlan = async (e) => {
    e.preventDefault();
    await saveRecord(() => {
      const fd = new FormData();
      if (planForm.id) fd.append('id', planForm.id);
      fd.append('record_type', 'plan_document');
      fd.append('category', 'plan_estrategico');
      fd.append('title', planForm.title);
      fd.append('activity_date', planForm.activity_date);
      fd.append('responsible_name', planForm.responsible_name);
      fd.append('notes', planForm.notes);
      appendExistingAttachments(fd, planForm.existingAttachments);
      appendFiles(fd, planForm.files);
      return fd;
    }, () => setPlanForm(createPlanForm()));
  };

  const handleSaveVehicleDocument = async (e) => {
    e.preventDefault();
    const vehicle = vehiclesById.get(String(vehicleDocForm.related_vehicle_id || ''));
    await saveRecord(() => {
      const fd = new FormData();
      if (vehicleDocForm.id) fd.append('id', vehicleDocForm.id);
      fd.append('record_type', 'vehicle_document');
      fd.append('category', vehicleDocForm.document_type);
      fd.append('title', vehicleDocForm.document_type);
      fd.append('related_vehicle_id', String(vehicleDocForm.related_vehicle_id || ''));
      fd.append('vehicle_plate', String(vehicle?.plate || ''));
      fd.append('due_date', vehicleDocForm.due_date);
      fd.append('status', vehicleDocForm.status);
      fd.append('notes', vehicleDocForm.notes);
      appendExistingAttachments(fd, vehicleDocForm.existingAttachments);
      appendFiles(fd, vehicleDocForm.files);
      return fd;
    }, () => setVehicleDocForm(createVehicleDocForm()));
  };

  const handleInfractionCatalogChange = (name) => {
    const found = asArray(infractionCatalog).find((item) => item.name === name);
    setInfractionForm((prev) => ({
      ...prev,
      infraction_name: name,
      infraction_code: found?.code || '',
    }));
  };

  const handleSaveInfraction = async (e) => {
    e.preventDefault();
    const driver = driversById.get(String(infractionForm.related_driver_id || ''));
    const vehicle = vehiclesById.get(String(infractionForm.related_vehicle_id || ''));
    await saveRecord(() => {
      const fd = new FormData();
      if (infractionForm.id) fd.append('id', infractionForm.id);
      fd.append('record_type', 'infraction_record');
      fd.append('category', infractionForm.infraction_name);
      fd.append('title', infractionForm.infraction_name);
      fd.append('code', infractionForm.infraction_code);
      fd.append('related_driver_id', String(infractionForm.related_driver_id || ''));
      fd.append('driver_name', String(driver?.nombre || ''));
      fd.append('driver_doc', String(driver?.cedula || ''));
      fd.append('related_vehicle_id', String(infractionForm.related_vehicle_id || ''));
      fd.append('vehicle_plate', String(vehicle?.plate || ''));
      fd.append('activity_date', infractionForm.activity_date);
      fd.append('status', infractionForm.status);
      fd.append('notes', infractionForm.notes);
      fd.append(
        'payload_json',
        JSON.stringify({
          authority: infractionForm.authority,
          amount: infractionForm.amount,
          location: infractionForm.location,
          ticket_number: infractionForm.ticket_number,
        }),
      );
      appendExistingAttachments(fd, infractionForm.existingAttachments);
      appendFiles(fd, infractionForm.files);
      return fd;
    }, () => setInfractionForm(createInfractionForm()));
  };

  const handleSaveAutogestion = async (e) => {
    e.preventDefault();
    await saveRecord(() => {
      const fd = new FormData();
      if (autogestionForm.id) fd.append('id', autogestionForm.id);
      fd.append('record_type', 'autogestion_report');
      fd.append('category', 'autogestion');
      fd.append('title', autogestionForm.title);
      fd.append('activity_date', autogestionForm.activity_date);
      fd.append('period_year', autogestionForm.period_year);
      fd.append('period_label', autogestionForm.period_label);
      fd.append('responsible_name', autogestionForm.responsible_name);
      fd.append('status', autogestionForm.status);
      fd.append('notes', autogestionForm.notes);
      appendExistingAttachments(fd, autogestionForm.existingAttachments);
      appendFiles(fd, autogestionForm.files);
      return fd;
    }, () => setAutogestionForm(createAutogestionForm()));
  };

  const handleDeleteRecord = async (id) => {
    if (!window.confirm('¿Deseas eliminar este registro del PESV?')) return;
    resetMessage();
    try {
      await api.pesv.delete({ id });
      setMessage({ type: 'success', text: 'Registro eliminado correctamente.' });
      await loadRecords();
    } catch (error) {
      setMessage({ type: 'error', text: error?.message || 'No fue posible eliminar el registro.' });
    }
  };

  const vehicleDocuments = useMemo(() => {
    if (!vehicleDocForm.related_vehicle_id) return asArray(records.vehicle_document);
    return asArray(records.vehicle_document).filter(
      (item) => String(item.related_vehicle_id || '') === String(vehicleDocForm.related_vehicle_id),
    );
  }, [records.vehicle_document, vehicleDocForm.related_vehicle_id]);

  const filteredPrograms = useMemo(
    () => asArray(records.program_activity).filter((item) =>
      includesSearch([item?.category, item?.title, item?.responsible_name, item?.notes], programFilters.text)
      && inDateRange(item?.activity_date, programFilters.from, programFilters.to)),
    [records.program_activity, programFilters],
  );

  const filteredIndicators = useMemo(
    () => asArray(records.indicator_record).filter((item) =>
      includesSearch([item?.code, item?.category, item?.title, item?.responsible_name, item?.period_label], indicatorFilters.text)
      && inDateRange(item?.activity_date, indicatorFilters.from, indicatorFilters.to)),
    [records.indicator_record, indicatorFilters],
  );

  const filteredPlans = useMemo(
    () => asArray(records.plan_document).filter((item) =>
      includesSearch([item?.title, item?.responsible_name, item?.notes], planFilters.text)
      && inDateRange(item?.activity_date, planFilters.from, planFilters.to)),
    [records.plan_document, planFilters],
  );

  const filteredVehicleDocuments = useMemo(
    () => vehicleDocuments.filter((item) =>
      (!vehicleDocFilters.vehicleId || String(item?.related_vehicle_id || '') === String(vehicleDocFilters.vehicleId))
      && includesSearch([item?.vehicle_plate, item?.category, item?.notes, item?.status], vehicleDocFilters.text)
      && inDateRange(item?.due_date, vehicleDocFilters.from, vehicleDocFilters.to)),
    [vehicleDocuments, vehicleDocFilters],
  );

  const vehicleHistoryTypeOptions = useMemo(
    () => Array.from(new Set(asArray(vehicleHistory).map((item) => String(item?.type || '').trim()).filter(Boolean))).sort(),
    [vehicleHistory],
  );

  const filteredVehicleHistory = useMemo(
    () => asArray(vehicleHistory).filter((item) => {
      const visualState = getMaintenanceHistoryVisualState(item);
      return (!vehicleHistoryFilters.type || String(item?.type || '') === vehicleHistoryFilters.type)
        && (!vehicleHistoryFilters.state || visualState.label === vehicleHistoryFilters.state)
        && inDateRange(String(item?.start_date || item?.date || ''), vehicleHistoryFilters.from, vehicleHistoryFilters.to);
    }),
    [vehicleHistory, vehicleHistoryFilters],
  );

  const vehicleHistorySummary = useMemo(() => {
    const summary = {
      total: filteredVehicleHistory.length,
      atrasado: 0,
      enCurso: 0,
      programado: 0,
      ejecutado: 0,
      historico: 0,
    };

    filteredVehicleHistory.forEach((item) => {
      const label = getMaintenanceHistoryVisualState(item).label;
      if (label === 'Atrasado') summary.atrasado += 1;
      else if (label === 'En curso') summary.enCurso += 1;
      else if (label === 'Programado' || label === 'Proximo') summary.programado += 1;
      else if (label === 'Ejecutado') summary.ejecutado += 1;
      else summary.historico += 1;
    });

    return summary;
  }, [filteredVehicleHistory]);

  const filteredInfractions = useMemo(
    () => asArray(records.infraction_record).filter((item) =>
      (!infractionFilters.vehicleId || String(item?.related_vehicle_id || '') === String(infractionFilters.vehicleId))
      && (!infractionFilters.driverId || String(item?.related_driver_id || '') === String(infractionFilters.driverId))
      && includesSearch([item?.vehicle_plate, item?.driver_name, item?.driver_doc, item?.category, item?.code, item?.status, item?.payload?.ticket_number], infractionFilters.text)
      && inDateRange(item?.activity_date, infractionFilters.from, infractionFilters.to)),
    [records.infraction_record, infractionFilters],
  );

  const filteredAutogestion = useMemo(
    () => asArray(records.autogestion_report).filter((item) =>
      includesSearch([item?.title, item?.responsible_name, item?.period_label, item?.notes, item?.status], autogestionFilters.text)
      && inDateRange(item?.activity_date, autogestionFilters.from, autogestionFilters.to)),
    [records.autogestion_report, autogestionFilters],
  );

  const stats = useMemo(() => ({
    programs: asArray(records.program_activity).length,
    indicators: asArray(records.indicator_record).length,
    vehicleDocs: asArray(records.vehicle_document).length,
    infractions: asArray(records.infraction_record).length,
  }), [records]);

  const pesvAlerts = useMemo(() => {
    const alerts = [];

    // Fecha vigente de cada documento de la carpeta PESV por vehículo y
    // categoría: la de vencimiento MÁS LEJANA (no la última fila creada:
    // un registro viejo cargado después no debe volver a poner la alerta).
    const dateKey = (d) => String(d || '').slice(0, 10);
    const latestVehicleDocs = new Map();
    asArray(records.vehicle_document).forEach((item) => {
      if (String(item?.status || '').trim().toUpperCase() === 'NO VIGENTE' && !item?.due_date) return;
      const key = `${item?.related_vehicle_id ?? ''}|${String(item?.category || '').trim().toLowerCase()}`;
      const prev = latestVehicleDocs.get(key);
      if (!prev || (dateKey(item?.due_date) > dateKey(prev?.due_date))) latestVehicleDocs.set(key, item);
    });
    // SOAT / RTM / Póliza existen en la ficha del vehículo y en la carpeta:
    // se usa la fecha más reciente de las dos y se genera una sola alerta.
    const FICHA_DOCS = [
      { key: 'soat_expiry', label: 'SOAT', category: 'soat' },
      { key: 'tecnomecanica_expiry', label: 'RTM', category: 'rtm' },
      { key: 'policy_expiry', label: 'Poliza', category: 'poliza' },
    ];

    asArray(vehicles).forEach((vehicle) => {
      if (inactiveVehicleIds.has(String(vehicle?.id))) return;
      FICHA_DOCS.forEach((doc) => {
        const folderDate = dateKey(latestVehicleDocs.get(`${vehicle?.id ?? ''}|${doc.category}`)?.due_date);
        const fichaDate = dateKey(vehicle?.[doc.key]);
        const effectiveDate = folderDate > fichaDate ? folderDate : fichaDate;
        const days = diffDaysFromToday(effectiveDate);
        const meta = getAlertMeta(days);
        if (!meta) return;
        alerts.push({
          id: `veh-${vehicle?.id}-${doc.key}`,
          source: 'vehiculo',
          section: 'Vehiculos',
          title: `${doc.label} ${String(vehicle?.plate || '').trim() || 'Sin placa'}`,
          subtitle: `${doc.label} del vehiculo ${String(vehicle?.plate || '').trim() || 'Sin placa'}`,
          date: effectiveDate || '',
          days,
          level: meta.level,
          levelLabel: meta.label,
          order: meta.order,
        });
      });

      if (!vehicle?.has_documents) {
        alerts.push({
          id: `veh-${vehicle?.id}-folder`,
          source: 'vehiculo',
          section: 'Vehiculos',
          title: `Carpeta incompleta ${String(vehicle?.plate || '').trim() || 'Sin placa'}`,
          subtitle: `El vehiculo ${String(vehicle?.plate || '').trim() || 'Sin placa'} no tiene documentos base marcados`,
          date: '',
          days: -1,
          level: 'warning',
          levelLabel: 'Pendiente',
          order: 2,
        });
      }
    });

    const fichaCategories = new Set(FICHA_DOCS.map((d) => d.category));
    const knownVehicleIds = new Set(asArray(vehicles).map((v) => String(v?.id ?? '')));
    latestVehicleDocs.forEach((item) => {
      if (inactiveVehicleIds.has(String(item?.related_vehicle_id ?? ''))) return;
      // Ya se alertó arriba junto con la ficha del vehículo.
      if (fichaCategories.has(String(item?.category || '').trim().toLowerCase())
        && knownVehicleIds.has(String(item?.related_vehicle_id ?? ''))) return;
      const days = diffDaysFromToday(item?.due_date);
      const meta = getAlertMeta(days);
      if (!meta) return;
      alerts.push({
        id: `pesv-doc-${item?.id || `${item?.related_vehicle_id}-${item?.category}-${item?.due_date}`}`,
        source: 'pesv',
        section: 'Carpeta PESV',
        title: `${item?.category || 'Documento'} ${item?.vehicle_plate || ''}`.trim(),
        subtitle: `Documento cargado en carpeta individual del vehiculo ${item?.vehicle_plate || ''}`.trim(),
        date: item?.due_date || '',
        days,
        level: meta.level,
        levelLabel: meta.label,
        order: meta.order,
      });
    });

    return alerts.sort((a, b) => {
      if (a.order !== b.order) return a.order - b.order;
      if ((a.days ?? 9999) !== (b.days ?? 9999)) return (a.days ?? 9999) - (b.days ?? 9999);
      return String(a.title || '').localeCompare(String(b.title || ''));
    });
  }, [vehicles, records.vehicle_document, inactiveVehicleIds]);

  const alertStats = useMemo(() => ({
    total: pesvAlerts.length,
    expired: pesvAlerts.filter((item) => item.level === 'expired').length,
    critical: pesvAlerts.filter((item) => item.level === 'critical').length,
    warning: pesvAlerts.filter((item) => item.level === 'warning').length,
  }), [pesvAlerts]);

  const exportRows = (rows, sheetName, fileName) => {
    try {
      const safeRows = asArray(rows);
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(safeRows.length ? safeRows : [{ INFO: 'Sin datos para exportar' }]);
      XLSX.utils.book_append_sheet(wb, ws, sheetName);
      void recordOperationsDownload({ fileType: 'xlsx', moduleKey: 'transportes', reportName: sheetName });
      XLSX.writeFile(wb, fileName);
    } catch (error) {
      console.error(`Error exportando ${sheetName} a Excel`, error);
      setMessage({ type: 'error', text: `No fue posible exportar ${sheetName} a Excel.` });
    }
  };

  const exportWorkbook = (sheets, fileName) => {
    try {
      const wb = XLSX.utils.book_new();
      asArray(sheets).forEach((sheet) => {
        const safeRows = asArray(sheet?.rows);
        const ws = XLSX.utils.json_to_sheet(safeRows.length ? safeRows : [{ INFO: 'Sin datos para exportar' }]);
        XLSX.utils.book_append_sheet(wb, ws, String(sheet?.name || 'Hoja').slice(0, 31));
      });
      void recordOperationsDownload({ fileType: 'xlsx', moduleKey: 'transportes', reportName: 'Consolidado PESV' });
      XLSX.writeFile(wb, fileName);
    } catch (error) {
      console.error('Error exportando libro consolidado PESV', error);
      setMessage({ type: 'error', text: 'No fue posible exportar el consolidado del PESV.' });
    }
  };

  const handleExportPrograms = () => {
    exportRows(
      filteredPrograms.map((item) => ({
        PROGRAMA: item.category || '',
        ACTIVIDAD: item.title || '',
        FECHA_ACTIVIDAD: item.activity_date || '',
        FECHA_COMPROMISO: item.due_date || '',
        ESTADO: item.status || '',
        RESPONSABLE: item.responsible_name || '',
        OBSERVACIONES: item.notes || '',
        EVIDENCIAS: attachmentNames(item.attachments),
      })),
      'ProgramasPESV',
      sheetFileName('pesv_programas_filtrados'),
    );
  };

  const handleExportIndicators = () => {
    exportRows(
      filteredIndicators.map((item) => ({
        CODIGO: item.code || '',
        INDICADOR: item.category || item.title || '',
        FECHA_MEDICION: item.activity_date || '',
        PERIODO: item.period_label || '',
        ANIO: item.period_year || '',
        RESULTADO: item.metric_value ?? '',
        META: item.meta_value ?? '',
        RESPONSABLE: item.responsible_name || '',
        VARIABLES: item.payload?.variables_text || '',
        OBSERVACIONES: item.notes || '',
        SOPORTES: attachmentNames(item.attachments),
      })),
      'IndicadoresPESV',
      sheetFileName('pesv_indicadores_filtrados'),
    );
  };

  const handleExportPlans = () => {
    exportRows(
      filteredPlans.map((item) => ({
        DOCUMENTO: item.title || '',
        FECHA: item.activity_date || '',
        RESPONSABLE: item.responsible_name || '',
        OBSERVACIONES: item.notes || '',
        ARCHIVOS: attachmentNames(item.attachments),
      })),
      'PlanEstrategico',
      sheetFileName('pesv_plan_estrategico_filtrado'),
    );
  };

  const handleExportVehicleDocuments = () => {
    exportRows(
      filteredVehicleDocuments.map((item) => ({
        VEHICULO: item.vehicle_plate || '',
        DOCUMENTO: item.category || '',
        ESTADO: item.status || '',
        FECHA_VENCIMIENTO: item.due_date || '',
        OBSERVACIONES: item.notes || '',
        ARCHIVOS: attachmentNames(item.attachments),
      })),
      'DocsVehiculosPESV',
      sheetFileName('pesv_documentos_vehiculos_filtrados'),
    );
  };

  const handleExportVehicleHistory = () => {
    exportRows(
      filteredVehicleHistory.map((item) => {
        const visualState = getMaintenanceHistoryVisualState(item);
        return ({
        VEHICULO: selectedVehicleForSheet?.plate || '',
        FECHA: item.start_date || item.date || '',
        TIPO: item.type || '',
        DESCRIPCION: item.description || '',
        KILOMETRAJE: item.mileage || '',
        COSTO: item.cost ?? '',
        ESTADO: visualState.label,
        DETALLE_ESTADO: visualState.helper,
        FECHA_CIERRE: item.end_date || '',
        SOPORTES: attachmentNames(maintenanceSupportItems(item)),
        });
      }),
      'HojaVidaVehiculo',
      sheetFileName(`pesv_hoja_vida_${String(selectedVehicleForSheet?.plate || 'vehiculo').replace(/[^\w-]/g, '_')}`),
    );
  };

  const handleExportVehicleHistoryPdf = async () => {
    if (!selectedVehicleForSheet) return;

    const vehicleName = `${selectedVehicleForSheet.plate || ''} - ${selectedVehicleForSheet.brand || ''} ${selectedVehicleForSheet.model || ''}`.trim();
    const logoSrc = resolveAppAssetUrl(senerfiFormatoLogoUrl);
    const rowsHtml = filteredVehicleHistory.length
      ? filteredVehicleHistory.map((item) => {
        const visualState = getMaintenanceHistoryVisualState(item);
        return `
          <tr>
            <td>${escapeHtml(fmtDate(item.start_date || item.date))}</td>
            <td>${escapeHtml(item.type || '-')}</td>
            <td>${escapeHtml(item.description || '-')}</td>
            <td>${escapeHtml(item.mileage || '-')}</td>
            <td>${escapeHtml(fmtMoney(item.cost))}</td>
            <td>
              <div style="display:inline-block;padding:4px 8px;border-radius:999px;font-size:11px;font-weight:700;${visualState.label === 'Atrasado' ? 'background:#fee2e2;color:#b91c1c;border:1px solid #fecaca;' : visualState.label === 'En curso' || visualState.label === 'Proximo' ? 'background:#fef3c7;color:#b45309;border:1px solid #fde68a;' : visualState.label === 'Programado' ? 'background:#ffedd5;color:#c2410c;border:1px solid #fdba74;' : 'background:#dcfce7;color:#15803d;border:1px solid #bbf7d0;'}">
                ${escapeHtml(visualState.label)}
              </div>
              <div style="margin-top:4px;font-size:11px;color:#4b5563;">${escapeHtml(visualState.helper)}</div>
              ${item.end_date ? `<div style="margin-top:2px;font-size:11px;color:#6b7280;">Cierre: ${escapeHtml(fmtDate(item.end_date))}</div>` : ''}
            </td>
            <td>${escapeHtml(attachmentNames(maintenanceSupportItems(item)) || 'Sin soportes')}</td>
          </tr>
        `;
      }).join('')
      : '<tr><td colspan="7" style="text-align:center;padding:20px;color:#6b7280;">No hay mantenimientos para los filtros seleccionados.</td></tr>';

    const filterParts = [
      vehicleHistoryFilters.type ? `Tipo: ${vehicleHistoryFilters.type}` : 'Tipo: Todos',
      vehicleHistoryFilters.state ? `Estado: ${vehicleHistoryFilters.state}` : 'Estado: Todos',
      vehicleHistoryFilters.from ? `Desde: ${fmtDate(vehicleHistoryFilters.from)}` : null,
      vehicleHistoryFilters.to ? `Hasta: ${fmtDate(vehicleHistoryFilters.to)}` : null,
    ].filter(Boolean);

    const wrapper = document.createElement('div');
    wrapper.style.position = 'fixed';
    wrapper.style.left = '-20000px';
    wrapper.style.top = '0';
    wrapper.style.width = '1100px';
    wrapper.style.background = '#ffffff';
    wrapper.style.padding = '24px';
    wrapper.style.zIndex = '-1';
    wrapper.innerHTML = `
      <div style="font-family: Arial, sans-serif; color: #111827; background: #ffffff; width: 100%; box-sizing: border-box;">
        <div style="border:1px solid #d1d5db;border-radius:18px;overflow:hidden;background:#ffffff;">
          <div style="display:grid;grid-template-columns:180px 1fr 170px;min-height:118px;">
            <div style="display:flex;align-items:center;justify-content:center;border-right:1px solid #d1d5db;padding:14px;background:#ffffff;">
              ${logoSrc ? `<img src="${escapeHtml(logoSrc)}" alt="Senerfi" style="max-width:132px;max-height:72px;object-fit:contain;" />` : '<div style="font-size:22px;font-weight:800;color:#111827;">SENERFI</div>'}
            </div>
            <div style="display:flex;flex-direction:column;justify-content:center;padding:14px 16px;border-right:1px solid #d1d5db;">
              <div style="font-size:18px;font-weight:800;letter-spacing:.3px;">SENERFI S.A.S.</div>
              <div style="margin-top:6px;font-size:16px;font-weight:700;">REPORTE HOJA DE VIDA DE VEHICULO PESV</div>
              <div style="margin-top:6px;font-size:12px;color:#4b5563;">Proceso Transporte y Seguridad Vial</div>
            </div>
            <div style="display:flex;flex-direction:column;justify-content:center;padding:12px 14px;background:#f9fafb;font-size:11px;color:#374151;">
              <div><strong>Fecha:</strong> ${escapeHtml(new Date().toLocaleDateString('es-CO'))}</div>
              <div style="margin-top:6px;"><strong>Hora:</strong> ${escapeHtml(new Date().toLocaleTimeString('es-CO'))}</div>
              <div style="margin-top:6px;"><strong>Version:</strong> 1.0</div>
              <div style="margin-top:6px;"><strong>Codigo:</strong> PESV-HV</div>
            </div>
          </div>
        </div>
        <div style="border:1px solid #fed7aa;border-radius:20px;padding:24px;background:linear-gradient(90deg,#fff7ed 0%,#ffffff 100%);margin-top:14px;">
          <div style="display:flex;justify-content:space-between;align-items:flex-start;gap:16px;">
            <div>
              <div style="font-size:24px;font-weight:700;">Hoja de vida de vehiculo - PESV</div>
              <div style="margin-top:6px;font-size:14px;color:#4b5563;">Vehiculo: ${escapeHtml(vehicleName)}</div>
              <div style="margin-top:4px;font-size:13px;color:#6b7280;">Generado: ${escapeHtml(new Date().toLocaleString('es-CO'))}</div>
            </div>
            <div style="text-align:right;font-size:12px;color:#6b7280;">Filtros: ${escapeHtml(filterParts.join(' | '))}</div>
          </div>
          <div style="display:grid;grid-template-columns:repeat(5,1fr);gap:12px;margin-top:18px;">
            <div style="border:1px solid #e5e7eb;border-radius:14px;padding:14px;background:#f9fafb;">
              <div style="font-size:11px;text-transform:uppercase;color:#6b7280;">Total</div>
              <div style="margin-top:6px;font-size:28px;font-weight:700;color:#111827;">${vehicleHistorySummary.total}</div>
            </div>
            <div style="border:1px solid #fecaca;border-radius:14px;padding:14px;background:#fef2f2;">
              <div style="font-size:11px;text-transform:uppercase;color:#dc2626;">Atrasados</div>
              <div style="margin-top:6px;font-size:28px;font-weight:700;color:#b91c1c;">${vehicleHistorySummary.atrasado}</div>
            </div>
            <div style="border:1px solid #fde68a;border-radius:14px;padding:14px;background:#fffbeb;">
              <div style="font-size:11px;text-transform:uppercase;color:#b45309;">En curso</div>
              <div style="margin-top:6px;font-size:28px;font-weight:700;color:#b45309;">${vehicleHistorySummary.enCurso}</div>
            </div>
            <div style="border:1px solid #fdba74;border-radius:14px;padding:14px;background:#fff7ed;">
              <div style="font-size:11px;text-transform:uppercase;color:#c2410c;">Programados</div>
              <div style="margin-top:6px;font-size:28px;font-weight:700;color:#c2410c;">${vehicleHistorySummary.programado}</div>
            </div>
            <div style="border:1px solid #bbf7d0;border-radius:14px;padding:14px;background:#f0fdf4;">
              <div style="font-size:11px;text-transform:uppercase;color:#15803d;">Ejecutados</div>
              <div style="margin-top:6px;font-size:28px;font-weight:700;color:#15803d;">${vehicleHistorySummary.ejecutado}</div>
            </div>
          </div>
        </div>
        <div style="margin-top:18px;border:1px solid #e5e7eb;border-radius:18px;overflow:hidden;">
          <table style="width:100%;border-collapse:collapse;font-size:12px;">
            <thead>
              <tr style="background:#f9fafb;color:#4b5563;text-align:left;">
                <th style="padding:10px;border-bottom:1px solid #e5e7eb;">Fecha</th>
                <th style="padding:10px;border-bottom:1px solid #e5e7eb;">Tipo</th>
                <th style="padding:10px;border-bottom:1px solid #e5e7eb;">Descripcion</th>
                <th style="padding:10px;border-bottom:1px solid #e5e7eb;">Kilometraje</th>
                <th style="padding:10px;border-bottom:1px solid #e5e7eb;">Costo</th>
                <th style="padding:10px;border-bottom:1px solid #e5e7eb;">Estado</th>
                <th style="padding:10px;border-bottom:1px solid #e5e7eb;">Soportes</th>
              </tr>
            </thead>
            <tbody>
              ${rowsHtml}
            </tbody>
          </table>
        </div>
      </div>
    `;

    try {
      document.body.appendChild(wrapper);
      await new Promise((resolve) => setTimeout(resolve, 80));

      const canvas = await html2canvas(wrapper, {
        scale: 2,
        backgroundColor: '#FFFFFF',
        useCORS: true,
        windowWidth: 1200,
      });

      const pdf = new jsPDF({ orientation: 'p', unit: 'mm', format: 'a4' });
      const imgData = canvas.toDataURL('image/jpeg', 0.95);
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const margin = 6;
      const pxToMm = 0.26458333333333334;
      const mmW = (canvas.width / 2) * pxToMm;
      const mmH = (canvas.height / 2) * pxToMm;
      const maxW = pageW - (margin * 2);
      const pageInnerH = pageH - (margin * 2);
      const fitScale = Math.min(1, maxW / mmW);
      const drawW = mmW * fitScale;
      const drawH = mmH * fitScale;

      let offsetY = 0;
      pdf.addImage(imgData, 'JPEG', margin, margin - offsetY, drawW, drawH, undefined, 'FAST');
      while (offsetY + pageInnerH < drawH) {
        offsetY += pageInnerH;
        pdf.addPage();
        pdf.addImage(imgData, 'JPEG', margin, margin - offsetY, drawW, drawH, undefined, 'FAST');
      }

      void recordOperationsDownload({ fileType: 'pdf', moduleKey: 'transportes', reportName: 'Hoja de vida vehículo PESV' });
      pdf.save(pdfFileName(`pesv_hoja_vida_${String(selectedVehicleForSheet?.plate || 'vehiculo').replace(/[^\w-]/g, '_')}`));
    } catch (error) {
      console.error('Error exportando PDF de hoja de vida', error);
      setMessage({ type: 'error', text: 'No fue posible exportar la hoja de vida en PDF.' });
    } finally {
      try { wrapper.remove(); } catch (_) {}
    }
  };

  const handleExportInfractions = () => {
    exportRows(
      filteredInfractions.map((item) => ({
        CONDUCTOR: item.driver_name || '',
        CEDULA: item.driver_doc || '',
        VEHICULO: item.vehicle_plate || '',
        CODIGO: item.code || '',
        INFRACCION: item.category || '',
        FECHA: item.activity_date || '',
        ESTADO: item.status || '',
        AUTORIDAD: item.payload?.authority || '',
        VALOR: item.payload?.amount ?? '',
        LUGAR: item.payload?.location || '',
        COMPARENDO: item.payload?.ticket_number || '',
        OBSERVACIONES: item.notes || '',
        EVIDENCIAS: attachmentNames(item.attachments),
      })),
      'InfraccionesPESV',
      sheetFileName('pesv_infracciones_filtradas'),
    );
  };

  const handleExportAutogestion = () => {
    exportRows(
      filteredAutogestion.map((item) => ({
        REPORTE: item.title || '',
        FECHA: item.activity_date || '',
        PERIODO: item.period_label || '',
        ANIO: item.period_year || '',
        ESTADO: item.status || '',
        RESPONSABLE: item.responsible_name || '',
        OBSERVACIONES: item.notes || '',
        SOPORTES: attachmentNames(item.attachments),
      })),
      'AutogestionPESV',
      sheetFileName('pesv_autogestion_filtrada'),
    );
  };

  const handleExportAllPesvPdf = async () => {
    const logoSrc = resolveAppAssetUrl(senerfiFormatoLogoUrl);
    const vehicleHistoryRows = selectedVehicleForSheet
      ? filteredVehicleHistory.map((item) => {
        const visualState = getMaintenanceHistoryVisualState(item);
        return {
          FECHA: fmtDate(item.start_date || item.date),
          TIPO: item.type || '-',
          DESCRIPCION: item.description || '-',
          KILOMETRAJE: item.mileage || '-',
          COSTO: fmtMoney(item.cost),
          ESTADO: visualState.label,
          DETALLE: visualState.helper,
          CIERRE: fmtDate(item.end_date),
          SOPORTES: attachmentNames(maintenanceSupportItems(item)) || 'Sin soportes',
        };
      })
      : [];

    const sections = [
      {
        title: 'Programas del PESV',
        subtitle: `${filteredPrograms.length} registro(s)`,
        table: buildPdfTable(
          filteredPrograms.map((item) => ({
            PROGRAMA: item.category || '',
            ACTIVIDAD: item.title || '',
            FECHA: fmtDate(item.activity_date),
            COMPROMISO: fmtDate(item.due_date),
            ESTADO: item.status || '',
            RESPONSABLE: item.responsible_name || '',
          })),
          [
            { key: 'PROGRAMA', label: 'Programa' },
            { key: 'ACTIVIDAD', label: 'Actividad' },
            { key: 'FECHA', label: 'Fecha' },
            { key: 'COMPROMISO', label: 'Compromiso' },
            { key: 'ESTADO', label: 'Estado' },
            { key: 'RESPONSABLE', label: 'Responsable' },
          ],
        ),
      },
      {
        title: 'Indicadores del PESV',
        subtitle: `${filteredIndicators.length} registro(s)`,
        table: buildPdfTable(
          filteredIndicators.map((item) => ({
            CODIGO: item.code || '',
            INDICADOR: item.category || item.title || '',
            FECHA: fmtDate(item.activity_date),
            PERIODO: item.period_label || '',
            RESULTADO: item.metric_value ?? '',
            META: item.meta_value ?? '',
          })),
          [
            { key: 'CODIGO', label: 'Codigo' },
            { key: 'INDICADOR', label: 'Indicador' },
            { key: 'FECHA', label: 'Fecha' },
            { key: 'PERIODO', label: 'Periodo' },
            { key: 'RESULTADO', label: 'Resultado' },
            { key: 'META', label: 'Meta' },
          ],
        ),
      },
      {
        title: 'Plan Estrategico',
        subtitle: `${filteredPlans.length} documento(s)`,
        table: buildPdfTable(
          filteredPlans.map((item) => ({
            DOCUMENTO: item.title || '',
            FECHA: fmtDate(item.activity_date),
            RESPONSABLE: item.responsible_name || '',
            OBSERVACIONES: item.notes || '',
          })),
          [
            { key: 'DOCUMENTO', label: 'Documento' },
            { key: 'FECHA', label: 'Fecha' },
            { key: 'RESPONSABLE', label: 'Responsable' },
            { key: 'OBSERVACIONES', label: 'Observaciones' },
          ],
        ),
      },
      {
        title: 'Documentos por Vehiculo',
        subtitle: `${filteredVehicleDocuments.length} documento(s)`,
        table: buildPdfTable(
          filteredVehicleDocuments.map((item) => ({
            VEHICULO: item.vehicle_plate || '',
            DOCUMENTO: item.category || '',
            ESTADO: item.status || '',
            VENCE: fmtDate(item.due_date),
            OBSERVACIONES: item.notes || '',
          })),
          [
            { key: 'VEHICULO', label: 'Vehiculo' },
            { key: 'DOCUMENTO', label: 'Documento' },
            { key: 'ESTADO', label: 'Estado' },
            { key: 'VENCE', label: 'Vence' },
            { key: 'OBSERVACIONES', label: 'Observaciones' },
          ],
        ),
      },
      {
        title: 'Hoja de Vida de Vehiculo',
        subtitle: selectedVehicleForSheet ? `${selectedVehicleForSheet.plate} · ${vehicleHistoryRows.length} registro(s)` : 'Sin vehiculo seleccionado',
        table: selectedVehicleForSheet
          ? buildPdfTable(
            vehicleHistoryRows,
            [
              { key: 'FECHA', label: 'Fecha' },
              { key: 'TIPO', label: 'Tipo' },
              { key: 'DESCRIPCION', label: 'Descripcion' },
              { key: 'KILOMETRAJE', label: 'Kilometraje' },
              { key: 'COSTO', label: 'Costo' },
              { key: 'ESTADO', label: 'Estado' },
              { key: 'DETALLE', label: 'Detalle' },
            ],
          )
          : '<div style="padding:14px 16px;border:1px solid #e5e7eb;border-radius:14px;background:#f9fafb;color:#6b7280;font-size:12px;">Selecciona un vehiculo en la seccion Vehiculos y carpeta para incluir su hoja de vida.</div>',
      },
      {
        title: 'Matriz de Infracciones',
        subtitle: `${filteredInfractions.length} registro(s)`,
        table: buildPdfTable(
          filteredInfractions.map((item) => ({
            CONDUCTOR: item.driver_name || '',
            VEHICULO: item.vehicle_plate || '',
            CODIGO: item.code || '',
            INFRACCION: item.category || '',
            FECHA: fmtDate(item.activity_date),
            ESTADO: item.status || '',
          })),
          [
            { key: 'CONDUCTOR', label: 'Conductor' },
            { key: 'VEHICULO', label: 'Vehiculo' },
            { key: 'CODIGO', label: 'Codigo' },
            { key: 'INFRACCION', label: 'Infraccion' },
            { key: 'FECHA', label: 'Fecha' },
            { key: 'ESTADO', label: 'Estado' },
          ],
        ),
      },
      {
        title: 'Autogestion',
        subtitle: `${filteredAutogestion.length} registro(s)`,
        table: buildPdfTable(
          filteredAutogestion.map((item) => ({
            REPORTE: item.title || '',
            FECHA: fmtDate(item.activity_date),
            PERIODO: item.period_label || '',
            ESTADO: item.status || '',
            RESPONSABLE: item.responsible_name || '',
          })),
          [
            { key: 'REPORTE', label: 'Reporte' },
            { key: 'FECHA', label: 'Fecha' },
            { key: 'PERIODO', label: 'Periodo' },
            { key: 'ESTADO', label: 'Estado' },
            { key: 'RESPONSABLE', label: 'Responsable' },
          ],
        ),
      },
    ];

    const wrapper = document.createElement('div');
    wrapper.style.position = 'fixed';
    wrapper.style.left = '-20000px';
    wrapper.style.top = '0';
    wrapper.style.width = '1120px';
    wrapper.style.background = '#ffffff';
    wrapper.style.padding = '24px';
    wrapper.style.zIndex = '-1';
    wrapper.innerHTML = `
      <div style="font-family: Arial, sans-serif; color: #111827; background: #ffffff; width: 100%; box-sizing: border-box;">
        <div style="border:1px solid #d1d5db;border-radius:18px;overflow:hidden;background:#ffffff;">
          <div style="display:grid;grid-template-columns:180px 1fr 170px;min-height:118px;">
            <div style="display:flex;align-items:center;justify-content:center;border-right:1px solid #d1d5db;padding:14px;background:#ffffff;">
              ${logoSrc ? `<img src="${escapeHtml(logoSrc)}" alt="Senerfi" style="max-width:132px;max-height:72px;object-fit:contain;" />` : '<div style="font-size:22px;font-weight:800;color:#111827;">SENERFI</div>'}
            </div>
            <div style="display:flex;flex-direction:column;justify-content:center;padding:14px 16px;border-right:1px solid #d1d5db;">
              <div style="font-size:18px;font-weight:800;letter-spacing:.3px;">SENERFI S.A.S.</div>
              <div style="margin-top:6px;font-size:16px;font-weight:700;">REPORTE CONSOLIDADO PESV</div>
              <div style="margin-top:6px;font-size:12px;color:#4b5563;">Plan Estrategico de Seguridad Vial</div>
            </div>
            <div style="display:flex;flex-direction:column;justify-content:center;padding:12px 14px;background:#f9fafb;font-size:11px;color:#374151;">
              <div><strong>Fecha:</strong> ${escapeHtml(new Date().toLocaleDateString('es-CO'))}</div>
              <div style="margin-top:6px;"><strong>Hora:</strong> ${escapeHtml(new Date().toLocaleTimeString('es-CO'))}</div>
              <div style="margin-top:6px;"><strong>Version:</strong> 1.0</div>
              <div style="margin-top:6px;"><strong>Codigo:</strong> PESV-RPT</div>
            </div>
          </div>
        </div>
        <div style="border:1px solid #fed7aa;border-radius:20px;padding:24px;background:linear-gradient(90deg,#fff7ed 0%,#ffffff 100%);margin-top:14px;">
          <div style="display:flex;justify-content:space-between;align-items:flex-start;gap:16px;">
            <div>
              <div style="font-size:26px;font-weight:700;">Consolidado PESV</div>
              <div style="margin-top:6px;font-size:14px;color:#4b5563;">Resumen consolidado del modulo PESV con los filtros activos.</div>
              <div style="margin-top:4px;font-size:13px;color:#6b7280;">Generado: ${escapeHtml(new Date().toLocaleString('es-CO'))}</div>
            </div>
            <div style="text-align:right;font-size:12px;color:#6b7280;">
              Alertas activas: ${alertStats.total}<br />
              Hoja de vida: ${selectedVehicleForSheet ? escapeHtml(selectedVehicleForSheet.plate || '') : 'Sin vehiculo'}
            </div>
          </div>
          <div style="display:grid;grid-template-columns:repeat(4,1fr);gap:12px;margin-top:18px;">
            <div style="border:1px solid #fed7aa;border-radius:14px;padding:14px;background:#fff7ed;"><div style="font-size:11px;text-transform:uppercase;color:#c2410c;">Programas</div><div style="margin-top:6px;font-size:28px;font-weight:700;color:#9a3412;">${filteredPrograms.length}</div></div>
            <div style="border:1px solid #bfdbfe;border-radius:14px;padding:14px;background:#eff6ff;"><div style="font-size:11px;text-transform:uppercase;color:#1d4ed8;">Indicadores</div><div style="margin-top:6px;font-size:28px;font-weight:700;color:#1d4ed8;">${filteredIndicators.length}</div></div>
            <div style="border:1px solid #bbf7d0;border-radius:14px;padding:14px;background:#f0fdf4;"><div style="font-size:11px;text-transform:uppercase;color:#15803d;">Documentos</div><div style="margin-top:6px;font-size:28px;font-weight:700;color:#15803d;">${filteredVehicleDocuments.length}</div></div>
            <div style="border:1px solid #fde68a;border-radius:14px;padding:14px;background:#fffbeb;"><div style="font-size:11px;text-transform:uppercase;color:#b45309;">Infracciones</div><div style="margin-top:6px;font-size:28px;font-weight:700;color:#b45309;">${filteredInfractions.length}</div></div>
          </div>
        </div>
        <div style="display:grid;grid-template-columns:repeat(4,1fr);gap:12px;margin-top:16px;">
          <div style="border:1px solid #fecaca;border-radius:14px;padding:14px;background:#fef2f2;"><div style="font-size:11px;text-transform:uppercase;color:#dc2626;">Alertas vencidas</div><div style="margin-top:6px;font-size:26px;font-weight:700;color:#b91c1c;">${alertStats.expired}</div></div>
          <div style="border:1px solid #fde68a;border-radius:14px;padding:14px;background:#fffbeb;"><div style="font-size:11px;text-transform:uppercase;color:#b45309;">0 a 7 dias</div><div style="margin-top:6px;font-size:26px;font-weight:700;color:#b45309;">${alertStats.critical}</div></div>
          <div style="border:1px solid #fdba74;border-radius:14px;padding:14px;background:#fff7ed;"><div style="font-size:11px;text-transform:uppercase;color:#c2410c;">8 a 30 dias</div><div style="margin-top:6px;font-size:26px;font-weight:700;color:#c2410c;">${alertStats.warning}</div></div>
          <div style="border:1px solid #e5e7eb;border-radius:14px;padding:14px;background:#f9fafb;"><div style="font-size:11px;text-transform:uppercase;color:#6b7280;">Autogestion</div><div style="margin-top:6px;font-size:26px;font-weight:700;color:#111827;">${filteredAutogestion.length}</div></div>
        </div>
        ${sections.map((section) => `
          <div style="margin-top:18px;">
            <div style="display:flex;justify-content:space-between;align-items:end;gap:12px;margin-bottom:8px;">
              <div style="font-size:18px;font-weight:700;color:#111827;">${escapeHtml(section.title)}</div>
              <div style="font-size:12px;color:#6b7280;">${escapeHtml(section.subtitle)}</div>
            </div>
            ${section.table}
          </div>
        `).join('')}
      </div>
    `;

    try {
      document.body.appendChild(wrapper);
      await new Promise((resolve) => setTimeout(resolve, 100));

      const canvas = await html2canvas(wrapper, {
        scale: 1.6,
        backgroundColor: '#FFFFFF',
        useCORS: true,
        windowWidth: 1280,
      });

      const pdf = new jsPDF({ orientation: 'p', unit: 'mm', format: 'a4' });
      const imgData = canvas.toDataURL('image/jpeg', 0.92);
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const margin = 6;
      const pxToMm = 0.26458333333333334;
      const mmW = (canvas.width / 1.6) * pxToMm;
      const mmH = (canvas.height / 1.6) * pxToMm;
      const maxW = pageW - (margin * 2);
      const pageInnerH = pageH - (margin * 2);
      const fitScale = Math.min(1, maxW / mmW);
      const drawW = mmW * fitScale;
      const drawH = mmH * fitScale;

      let offsetY = 0;
      pdf.addImage(imgData, 'JPEG', margin, margin - offsetY, drawW, drawH, undefined, 'FAST');
      while (offsetY + pageInnerH < drawH) {
        offsetY += pageInnerH;
        pdf.addPage();
        pdf.addImage(imgData, 'JPEG', margin, margin - offsetY, drawW, drawH, undefined, 'FAST');
      }

      void recordOperationsDownload({ fileType: 'pdf', moduleKey: 'transportes', reportName: 'Consolidado PESV' });
      pdf.save(pdfFileName('pesv_consolidado'));
    } catch (error) {
      console.error('Error exportando PDF consolidado PESV', error);
      setMessage({ type: 'error', text: 'No fue posible exportar el consolidado del PESV en PDF.' });
    } finally {
      try { wrapper.remove(); } catch (_) {}
    }
  };

  const handleExportAllPesv = () => {
    const vehicleHistoryRows = filteredVehicleHistory.map((item) => {
      const visualState = getMaintenanceHistoryVisualState(item);
      return ({
      VEHICULO: selectedVehicleForSheet?.plate || '',
      FECHA: item.start_date || item.date || '',
      TIPO: item.type || '',
      DESCRIPCION: item.description || '',
      KILOMETRAJE: item.mileage || '',
      COSTO: item.cost ?? '',
      ESTADO: visualState.label,
      DETALLE_ESTADO: visualState.helper,
      FECHA_CIERRE: item.end_date || '',
      SOPORTES: attachmentNames(maintenanceSupportItems(item)),
      });
    });

    exportWorkbook([
      {
        name: 'Programas',
        rows: filteredPrograms.map((item) => ({
          PROGRAMA: item.category || '',
          ACTIVIDAD: item.title || '',
          FECHA_ACTIVIDAD: item.activity_date || '',
          FECHA_COMPROMISO: item.due_date || '',
          ESTADO: item.status || '',
          RESPONSABLE: item.responsible_name || '',
          OBSERVACIONES: item.notes || '',
          EVIDENCIAS: attachmentNames(item.attachments),
        })),
      },
      {
        name: 'Indicadores',
        rows: filteredIndicators.map((item) => ({
          CODIGO: item.code || '',
          INDICADOR: item.category || item.title || '',
          FECHA_MEDICION: item.activity_date || '',
          PERIODO: item.period_label || '',
          ANIO: item.period_year || '',
          RESULTADO: item.metric_value ?? '',
          META: item.meta_value ?? '',
          RESPONSABLE: item.responsible_name || '',
          VARIABLES: item.payload?.variables_text || '',
          OBSERVACIONES: item.notes || '',
          SOPORTES: attachmentNames(item.attachments),
        })),
      },
      {
        name: 'PlanEstrategico',
        rows: filteredPlans.map((item) => ({
          DOCUMENTO: item.title || '',
          FECHA: item.activity_date || '',
          RESPONSABLE: item.responsible_name || '',
          OBSERVACIONES: item.notes || '',
          ARCHIVOS: attachmentNames(item.attachments),
        })),
      },
      {
        name: 'DocsVehiculos',
        rows: filteredVehicleDocuments.map((item) => ({
          VEHICULO: item.vehicle_plate || '',
          DOCUMENTO: item.category || '',
          ESTADO: item.status || '',
          FECHA_VENCIMIENTO: item.due_date || '',
          OBSERVACIONES: item.notes || '',
          ARCHIVOS: attachmentNames(item.attachments),
        })),
      },
      {
        name: 'HojaVidaVehiculo',
        rows: selectedVehicleForSheet
          ? vehicleHistoryRows
          : [{ INFO: 'Selecciona un vehículo en la sección de Vehículos y carpeta para incluir su hoja de vida.' }],
      },
      {
        name: 'Infracciones',
        rows: filteredInfractions.map((item) => ({
          CONDUCTOR: item.driver_name || '',
          CEDULA: item.driver_doc || '',
          VEHICULO: item.vehicle_plate || '',
          CODIGO: item.code || '',
          INFRACCION: item.category || '',
          FECHA: item.activity_date || '',
          ESTADO: item.status || '',
          AUTORIDAD: item.payload?.authority || '',
          VALOR: item.payload?.amount ?? '',
          LUGAR: item.payload?.location || '',
          COMPARENDO: item.payload?.ticket_number || '',
          OBSERVACIONES: item.notes || '',
          EVIDENCIAS: attachmentNames(item.attachments),
        })),
      },
      {
        name: 'Autogestion',
        rows: filteredAutogestion.map((item) => ({
          REPORTE: item.title || '',
          FECHA: item.activity_date || '',
          PERIODO: item.period_label || '',
          ANIO: item.period_year || '',
          ESTADO: item.status || '',
          RESPONSABLE: item.responsible_name || '',
          OBSERVACIONES: item.notes || '',
          SOPORTES: attachmentNames(item.attachments),
        })),
      },
    ], sheetFileName('pesv_consolidado'));
  };

  return (
    <div className="space-y-6 p-4">
      <div className="rounded-2xl border border-orange-100 bg-gradient-to-r from-orange-50 to-white p-5 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <div className="text-xl font-semibold text-gray-900">Modulo PESV</div>
            <div className="mt-1 text-sm text-gray-600">
              Gestiona programas, indicadores, plan estratégico, hoja de vida vehicular, infracciones y soportes del PESV.
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={handleExportAllPesvPdf}
              className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              <FileText className="h-4 w-4" />
              Exportar PDF PESV
            </button>
            <button
              type="button"
              onClick={handleExportAllPesv}
              className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              <Download className="h-4 w-4" />
              Exportar Excel PESV
            </button>
            <button
              type="button"
              onClick={loadEverything}
              className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              <RefreshCw className="h-4 w-4" />
              Recargar
            </button>
          </div>
        </div>

        <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
          <div className="rounded-xl border border-orange-200 bg-white px-4 py-3">
            <div className="text-xs uppercase tracking-wide text-gray-500">Programas</div>
            <div className="mt-1 text-2xl font-semibold text-gray-900">{stats.programs}</div>
          </div>
          <div className="rounded-xl border border-blue-200 bg-white px-4 py-3">
            <div className="text-xs uppercase tracking-wide text-gray-500">Indicadores</div>
            <div className="mt-1 text-2xl font-semibold text-gray-900">{stats.indicators}</div>
          </div>
          <div className="rounded-xl border border-emerald-200 bg-white px-4 py-3">
            <div className="text-xs uppercase tracking-wide text-gray-500">Docs vehículos</div>
            <div className="mt-1 text-2xl font-semibold text-gray-900">{stats.vehicleDocs}</div>
          </div>
          <div className="rounded-xl border border-yellow-200 bg-white px-4 py-3">
            <div className="text-xs uppercase tracking-wide text-gray-500">Infracciones</div>
            <div className="mt-1 text-2xl font-semibold text-gray-900">{stats.infractions}</div>
          </div>
        </div>

        <div className="mt-4 rounded-2xl border border-red-100 bg-white p-4 shadow-sm">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <div className="flex items-center gap-2 text-base font-semibold text-gray-900">
                <AlertTriangle className="h-5 w-5 text-red-500" />
                Tablero de alertas PESV
              </div>
              <div className="mt-1 text-sm text-gray-600">
                Consolida vencimientos de SOAT, RTM, polizas y documentos cargados en la carpeta individual de vehiculos.
              </div>
            </div>
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
              <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3">
                <div className="text-xs uppercase tracking-wide text-red-600">Vencidos</div>
                <div className="mt-1 text-2xl font-semibold text-red-700">{alertStats.expired}</div>
              </div>
              <div className="rounded-xl border border-yellow-200 bg-yellow-50 px-4 py-3">
                <div className="text-xs uppercase tracking-wide text-yellow-700">0 a 7 días</div>
                <div className="mt-1 text-2xl font-semibold text-yellow-700">{alertStats.critical}</div>
              </div>
              <div className="rounded-xl border border-orange-200 bg-orange-50 px-4 py-3">
                <div className="text-xs uppercase tracking-wide text-orange-700">8 a 30 días</div>
                <div className="mt-1 text-2xl font-semibold text-orange-700">{alertStats.warning}</div>
              </div>
              <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3">
                <div className="text-xs uppercase tracking-wide text-gray-500">Total alertas</div>
                <div className="mt-1 text-2xl font-semibold text-gray-900">{alertStats.total}</div>
              </div>
            </div>
          </div>

          <div className="mt-4 grid gap-3 xl:grid-cols-2">
            {pesvAlerts.length === 0 ? (
              <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-4 text-sm text-emerald-700">
                No hay vencimientos ni alertas activas en este momento.
              </div>
            ) : pesvAlerts.slice(0, 8).map((alert) => (
              <div key={alert.id} className={`rounded-xl border px-4 py-3 ${alertClasses(alert.level)}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="font-semibold">{alert.title}</div>
                  <span className="rounded-full border border-current px-2 py-1 text-xs font-semibold">
                    {alert.levelLabel}
                  </span>
                </div>
                <div className="mt-1 text-sm">{alert.subtitle}</div>
                <div className="mt-2 flex flex-wrap gap-3 text-xs font-medium">
                  <span>Sección: {alert.section}</span>
                  <span>Fecha: {fmtDate(alert.date)}</span>
                  <span>{alertTextFromDays(alert.days)}</span>
                </div>
              </div>
            ))}
          </div>

          {pesvAlerts.length > 8 ? (
            <div className="mt-3 text-xs text-gray-500">
              Se muestran las 8 alertas más prioritarias de un total de {alertStats.total}.
            </div>
          ) : null}
        </div>
      </div>

      {message && (
        <div className={`rounded-xl border px-4 py-3 text-sm ${
          message.type === 'success'
            ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
            : 'border-red-200 bg-red-50 text-red-700'
        }`}>
          {message.text}
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        <TabButton active={activeTab === 'programas'} icon={ClipboardList} label="Programas del PESV" onClick={() => setActiveTab('programas')} />
        <TabButton active={activeTab === 'indicadores'} icon={ShieldCheck} label="Indicadores del PESV" onClick={() => setActiveTab('indicadores')} />
        <TabButton active={activeTab === 'plan'} icon={FileText} label="Plan Estratégico" onClick={() => setActiveTab('plan')} />
        <TabButton active={activeTab === 'vehiculos'} icon={Car} label="Vehículos y carpeta" onClick={() => setActiveTab('vehiculos')} />
        <TabButton active={activeTab === 'infracciones'} icon={AlertTriangle} label="Matriz de infracciones" onClick={() => setActiveTab('infracciones')} />
        <TabButton active={activeTab === 'autogestion'} icon={Users} label="Autogestión" onClick={() => setActiveTab('autogestion')} />
      </div>

      {loading ? (
        <div className="rounded-xl border border-gray-200 bg-white px-4 py-10 text-center text-sm text-gray-500">
          Cargando información del PESV...
        </div>
      ) : null}

      {!loading && activeTab === 'programas' && (
        <div className="space-y-4">
          <form onSubmit={handleSaveProgram} className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
            <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div className="text-lg font-semibold text-gray-900">{programForm.id ? 'Editar actividad de programa' : 'Registrar actividad de programa'}</div>
              {programForm.id ? (
                <button type="button" onClick={() => setProgramForm(createProgramForm())} className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50">
                  <X className="h-4 w-4" />
                  Cancelar edición
                </button>
              ) : null}
            </div>
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              <label className="block">
                <span className="mb-1 block text-sm font-medium text-gray-700">Programa</span>
                <select className="w-full rounded-lg border border-gray-300 px-3 py-2" value={programForm.program_name} onChange={(e) => setProgramForm((prev) => ({ ...prev, program_name: e.target.value }))}>
                  {PROGRAM_OPTIONS.map((option) => <option key={option} value={option}>{option}</option>)}
                </select>
              </label>
              <label className="block md:col-span-2">
                <span className="mb-1 block text-sm font-medium text-gray-700">Actividad</span>
                <input className="w-full rounded-lg border border-gray-300 px-3 py-2" value={programForm.activity_name} onChange={(e) => setProgramForm((prev) => ({ ...prev, activity_name: e.target.value }))} required />
              </label>
              <label className="block">
                <span className="mb-1 block text-sm font-medium text-gray-700">Fecha actividad</span>
                <input type="date" className="w-full rounded-lg border border-gray-300 px-3 py-2" value={programForm.activity_date} onChange={(e) => setProgramForm((prev) => ({ ...prev, activity_date: e.target.value }))} />
              </label>
              <label className="block">
                <span className="mb-1 block text-sm font-medium text-gray-700">Fecha compromiso</span>
                <input type="date" className="w-full rounded-lg border border-gray-300 px-3 py-2" value={programForm.due_date} onChange={(e) => setProgramForm((prev) => ({ ...prev, due_date: e.target.value }))} />
              </label>
              <label className="block">
                <span className="mb-1 block text-sm font-medium text-gray-700">Estado</span>
                <select className="w-full rounded-lg border border-gray-300 px-3 py-2" value={programForm.status} onChange={(e) => setProgramForm((prev) => ({ ...prev, status: e.target.value }))}>
                  {PROGRAM_STATUS_OPTIONS.map((option) => <option key={option} value={option}>{option}</option>)}
                </select>
              </label>
              <label className="block xl:col-span-2">
                <span className="mb-1 block text-sm font-medium text-gray-700">Responsable</span>
                <input className="w-full rounded-lg border border-gray-300 px-3 py-2" value={programForm.responsible_name} onChange={(e) => setProgramForm((prev) => ({ ...prev, responsible_name: e.target.value }))} />
              </label>
              <label className="block">
                <span className="mb-1 block text-sm font-medium text-gray-700">Evidencias</span>
                <input type="file" multiple accept=".pdf,image/*" className="w-full rounded-lg border border-gray-300 px-3 py-2" onChange={(e) => setProgramForm((prev) => ({ ...prev, files: Array.from(e.target.files || []) }))} />
              </label>
              <label className="block md:col-span-2 xl:col-span-3">
                <span className="mb-1 block text-sm font-medium text-gray-700">Observaciones</span>
                <textarea rows={3} className="w-full rounded-lg border border-gray-300 px-3 py-2" value={programForm.notes} onChange={(e) => setProgramForm((prev) => ({ ...prev, notes: e.target.value }))} />
              </label>
            </div>
            {programForm.id && asArray(programForm.existingAttachments).length > 0 ? (
              <div className="mt-4">
                <div className="mb-2 text-sm font-medium text-gray-700">Archivos actuales</div>
                <FileLinks attachments={programForm.existingAttachments} />
              </div>
            ) : null}
            <div className="mt-4">
              <button type="submit" className="inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium text-white" style={{ backgroundColor: '#F07E2D' }}>
                <Save className="h-4 w-4" />
                {programForm.id ? 'Actualizar actividad' : 'Guardar actividad'}
              </button>
            </div>
          </form>

          <FiltersCard>
            <label className="block">
              <span className="mb-1 block text-sm font-medium text-gray-700">Buscar</span>
              <input className="w-full rounded-lg border border-gray-300 px-3 py-2" placeholder="Programa, actividad o responsable" value={programFilters.text} onChange={(e) => setProgramFilters((prev) => ({ ...prev, text: e.target.value }))} />
            </label>
            <label className="block">
              <span className="mb-1 block text-sm font-medium text-gray-700">Desde</span>
              <input type="date" className="w-full rounded-lg border border-gray-300 px-3 py-2" value={programFilters.from} onChange={(e) => setProgramFilters((prev) => ({ ...prev, from: e.target.value }))} />
            </label>
            <label className="block">
              <span className="mb-1 block text-sm font-medium text-gray-700">Hasta</span>
              <input type="date" className="w-full rounded-lg border border-gray-300 px-3 py-2" value={programFilters.to} onChange={(e) => setProgramFilters((prev) => ({ ...prev, to: e.target.value }))} />
            </label>
            <div className="flex items-end">
              <button type="button" onClick={() => setProgramFilters({ text: '', from: '', to: '' })} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50">
                Limpiar filtros
              </button>
            </div>
          </FiltersCard>

          <div className="rounded-2xl border border-gray-200 bg-white shadow-sm">
            <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
              <div className="text-lg font-semibold text-gray-900">Actividades registradas</div>
              <button type="button" onClick={handleExportPrograms} className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">
                <Download className="h-4 w-4" />
                Exportar
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-left text-gray-500">
                  <tr>
                    <th className="px-4 py-3">Programa</th>
                    <th className="px-4 py-3">Actividad</th>
                    <th className="px-4 py-3">Fechas</th>
                    <th className="px-4 py-3">Estado</th>
                    <th className="px-4 py-3">Responsable</th>
                    <th className="px-4 py-3">Evidencias</th>
                    <th className="px-4 py-3 text-center">Acción</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPrograms.length === 0 ? (
                    <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-500">No hay actividades del PESV registradas.</td></tr>
                  ) : filteredPrograms.map((item) => (
                    <tr key={item.id} className="border-t align-top">
                      <td className="px-4 py-4 font-medium text-gray-900">{item.category || '-'}</td>
                      <td className="px-4 py-4 text-gray-700">
                        <div>{item.title || '-'}</div>
                        {item.notes ? <div className="mt-1 text-xs text-gray-500">{item.notes}</div> : null}
                      </td>
                      <td className="px-4 py-4 text-gray-700">
                        <div>Actividad: {fmtDate(item.activity_date)}</div>
                        <div>Compromiso: {fmtDate(item.due_date)}</div>
                      </td>
                      <td className="px-4 py-4 text-gray-700">{item.status || '-'}</td>
                      <td className="px-4 py-4 text-gray-700">{item.responsible_name || '-'}</td>
                      <td className="px-4 py-4"><FileLinks attachments={item.attachments} /></td>
                      <td className="px-4 py-4 text-center">
                        <ActionButtons onEdit={() => handleEditProgram(item)} onDelete={() => handleDeleteRecord(item.id)} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {!loading && activeTab === 'indicadores' && (
        <div className="space-y-4">
          <form onSubmit={handleSaveIndicator} className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
            <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div className="text-lg font-semibold text-gray-900">{indicatorForm.id ? 'Editar indicador del PESV' : 'Registrar indicador del PESV'}</div>
              {indicatorForm.id ? (
                <button type="button" onClick={() => setIndicatorForm(createIndicatorForm())} className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50">
                  <X className="h-4 w-4" />
                  Cancelar edición
                </button>
              ) : null}
            </div>
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
              <label className="block">
                <span className="mb-1 block text-sm font-medium text-gray-700">Indicador</span>
                <select className="w-full rounded-lg border border-gray-300 px-3 py-2" value={indicatorForm.indicator_code} onChange={(e) => handleIndicatorCodeChange(e.target.value)}>
                  {PESV_INDICATORS.map((option) => <option key={option.code} value={option.code}>{option.code} - {option.name}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="mb-1 block text-sm font-medium text-gray-700">Fecha de medición</span>
                <input type="date" className="w-full rounded-lg border border-gray-300 px-3 py-2" value={indicatorForm.measurement_date} onChange={(e) => setIndicatorForm((prev) => ({ ...prev, measurement_date: e.target.value }))} />
              </label>
              <label className="block">
                <span className="mb-1 block text-sm font-medium text-gray-700">Año</span>
                <input className="w-full rounded-lg border border-gray-300 px-3 py-2" value={indicatorForm.period_year} onChange={(e) => setIndicatorForm((prev) => ({ ...prev, period_year: e.target.value }))} />
              </label>
              <label className="block">
                <span className="mb-1 block text-sm font-medium text-gray-700">Periodo</span>
                <input className="w-full rounded-lg border border-gray-300 px-3 py-2" placeholder="Ej: ene-mar 2026 o junio" value={indicatorForm.period_label} onChange={(e) => setIndicatorForm((prev) => ({ ...prev, period_label: e.target.value }))} />
              </label>
              <label className="block">
                <span className="mb-1 block text-sm font-medium text-gray-700">Resultado</span>
                <input className="w-full rounded-lg border border-gray-300 px-3 py-2" value={indicatorForm.result_value} onChange={(e) => setIndicatorForm((prev) => ({ ...prev, result_value: e.target.value }))} />
              </label>
              <label className="block">
                <span className="mb-1 block text-sm font-medium text-gray-700">Meta</span>
                <input className="w-full rounded-lg border border-gray-300 px-3 py-2" value={indicatorForm.target_value} onChange={(e) => setIndicatorForm((prev) => ({ ...prev, target_value: e.target.value }))} />
              </label>
              <label className="block xl:col-span-2">
                <span className="mb-1 block text-sm font-medium text-gray-700">Responsable</span>
                <input className="w-full rounded-lg border border-gray-300 px-3 py-2" value={indicatorForm.responsible_name} onChange={(e) => setIndicatorForm((prev) => ({ ...prev, responsible_name: e.target.value }))} />
              </label>
              <label className="block md:col-span-2 xl:col-span-4">
                <span className="mb-1 block text-sm font-medium text-gray-700">Variables y soporte del cálculo</span>
                <textarea rows={3} className="w-full rounded-lg border border-gray-300 px-3 py-2" value={indicatorForm.variables_text} onChange={(e) => setIndicatorForm((prev) => ({ ...prev, variables_text: e.target.value }))} placeholder={`Periodicidad: ${selectedIndicatorMeta.periodicity} | Unidad: ${selectedIndicatorMeta.unit}`} />
              </label>
              <label className="block md:col-span-2 xl:col-span-3">
                <span className="mb-1 block text-sm font-medium text-gray-700">Análisis / observaciones</span>
                <textarea rows={3} className="w-full rounded-lg border border-gray-300 px-3 py-2" value={indicatorForm.analysis} onChange={(e) => setIndicatorForm((prev) => ({ ...prev, analysis: e.target.value }))} />
              </label>
              <label className="block">
                <span className="mb-1 block text-sm font-medium text-gray-700">Evidencias</span>
                <input type="file" multiple accept=".pdf,image/*" className="w-full rounded-lg border border-gray-300 px-3 py-2" onChange={(e) => setIndicatorForm((prev) => ({ ...prev, files: Array.from(e.target.files || []) }))} />
              </label>
            </div>
            {indicatorForm.id && asArray(indicatorForm.existingAttachments).length > 0 ? (
              <div className="mt-4">
                <div className="mb-2 text-sm font-medium text-gray-700">Soportes actuales</div>
                <FileLinks attachments={indicatorForm.existingAttachments} />
              </div>
            ) : null}
            <div className="mt-4">
              <button type="submit" className="inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium text-white" style={{ backgroundColor: '#F07E2D' }}>
                <Save className="h-4 w-4" />
                {indicatorForm.id ? 'Actualizar indicador' : 'Guardar indicador'}
              </button>
            </div>
          </form>

          <FiltersCard>
            <label className="block">
              <span className="mb-1 block text-sm font-medium text-gray-700">Buscar</span>
              <input className="w-full rounded-lg border border-gray-300 px-3 py-2" placeholder="Código, indicador o responsable" value={indicatorFilters.text} onChange={(e) => setIndicatorFilters((prev) => ({ ...prev, text: e.target.value }))} />
            </label>
            <label className="block">
              <span className="mb-1 block text-sm font-medium text-gray-700">Desde</span>
              <input type="date" className="w-full rounded-lg border border-gray-300 px-3 py-2" value={indicatorFilters.from} onChange={(e) => setIndicatorFilters((prev) => ({ ...prev, from: e.target.value }))} />
            </label>
            <label className="block">
              <span className="mb-1 block text-sm font-medium text-gray-700">Hasta</span>
              <input type="date" className="w-full rounded-lg border border-gray-300 px-3 py-2" value={indicatorFilters.to} onChange={(e) => setIndicatorFilters((prev) => ({ ...prev, to: e.target.value }))} />
            </label>
            <div className="flex items-end">
              <button type="button" onClick={() => setIndicatorFilters({ text: '', from: '', to: '' })} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50">
                Limpiar filtros
              </button>
            </div>
          </FiltersCard>

          <div className="rounded-2xl border border-gray-200 bg-white shadow-sm">
            <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
              <div className="text-lg font-semibold text-gray-900">Mediciones registradas</div>
              <button type="button" onClick={handleExportIndicators} className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">
                <Download className="h-4 w-4" />
                Exportar
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-left text-gray-500">
                  <tr>
                    <th className="px-4 py-3">Indicador</th>
                    <th className="px-4 py-3">Periodo</th>
                    <th className="px-4 py-3">Resultado</th>
                    <th className="px-4 py-3">Meta</th>
                    <th className="px-4 py-3">Responsable</th>
                    <th className="px-4 py-3">Soportes</th>
                    <th className="px-4 py-3 text-center">Acción</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredIndicators.length === 0 ? (
                    <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-500">No hay indicadores del PESV registrados.</td></tr>
                  ) : filteredIndicators.map((item) => (
                    <tr key={item.id} className="border-t align-top">
                      <td className="px-4 py-4 text-gray-700">
                        <div className="font-medium text-gray-900">{item.code || '-'}</div>
                        <div>{item.category || item.title || '-'}</div>
                        {item.payload?.variables_text ? <div className="mt-1 text-xs text-gray-500">{item.payload.variables_text}</div> : null}
                      </td>
                      <td className="px-4 py-4 text-gray-700">
                        <div>{item.period_label || '-'}</div>
                        <div className="text-xs text-gray-500">Año: {item.period_year || '-'}</div>
                      </td>
                      <td className="px-4 py-4 text-gray-700">{item.metric_value ?? '-'}</td>
                      <td className="px-4 py-4 text-gray-700">{item.meta_value ?? '-'}</td>
                      <td className="px-4 py-4 text-gray-700">{item.responsible_name || '-'}</td>
                      <td className="px-4 py-4"><FileLinks attachments={item.attachments} /></td>
                      <td className="px-4 py-4 text-center">
                        <ActionButtons onEdit={() => handleEditIndicator(item)} onDelete={() => handleDeleteRecord(item.id)} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {!loading && activeTab === 'plan' && (
        <div className="space-y-4">
          <form onSubmit={handleSavePlan} className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
            <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div className="text-lg font-semibold text-gray-900">{planForm.id ? 'Editar documento del plan estratégico' : 'Cargue y consulta del plan estratégico'}</div>
              {planForm.id ? (
                <button type="button" onClick={() => setPlanForm(createPlanForm())} className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50">
                  <X className="h-4 w-4" />
                  Cancelar edición
                </button>
              ) : null}
            </div>
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
              <label className="block xl:col-span-2">
                <span className="mb-1 block text-sm font-medium text-gray-700">Nombre del documento</span>
                <input className="w-full rounded-lg border border-gray-300 px-3 py-2" value={planForm.title} onChange={(e) => setPlanForm((prev) => ({ ...prev, title: e.target.value }))} />
              </label>
              <label className="block">
                <span className="mb-1 block text-sm font-medium text-gray-700">Fecha</span>
                <input type="date" className="w-full rounded-lg border border-gray-300 px-3 py-2" value={planForm.activity_date} onChange={(e) => setPlanForm((prev) => ({ ...prev, activity_date: e.target.value }))} />
              </label>
              <label className="block">
                <span className="mb-1 block text-sm font-medium text-gray-700">Responsable</span>
                <input className="w-full rounded-lg border border-gray-300 px-3 py-2" value={planForm.responsible_name} onChange={(e) => setPlanForm((prev) => ({ ...prev, responsible_name: e.target.value }))} />
              </label>
              <label className="block md:col-span-2 xl:col-span-3">
                <span className="mb-1 block text-sm font-medium text-gray-700">Observaciones</span>
                <textarea rows={3} className="w-full rounded-lg border border-gray-300 px-3 py-2" value={planForm.notes} onChange={(e) => setPlanForm((prev) => ({ ...prev, notes: e.target.value }))} />
              </label>
              <label className="block">
                <span className="mb-1 block text-sm font-medium text-gray-700">PDF del plan</span>
                <input type="file" accept=".pdf,application/pdf" className="w-full rounded-lg border border-gray-300 px-3 py-2" onChange={(e) => setPlanForm((prev) => ({ ...prev, files: Array.from(e.target.files || []) }))} required={!planForm.id} />
              </label>
            </div>
            {planForm.id && asArray(planForm.existingAttachments).length > 0 ? (
              <div className="mt-4">
                <div className="mb-2 text-sm font-medium text-gray-700">PDF actual</div>
                <FileLinks attachments={planForm.existingAttachments} />
              </div>
            ) : null}
            <div className="mt-4">
              <button type="submit" className="inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium text-white" style={{ backgroundColor: '#F07E2D' }}>
                <Save className="h-4 w-4" />
                {planForm.id ? 'Actualizar PDF' : 'Guardar PDF'}
              </button>
            </div>
          </form>

          <FiltersCard>
            <label className="block">
              <span className="mb-1 block text-sm font-medium text-gray-700">Buscar</span>
              <input className="w-full rounded-lg border border-gray-300 px-3 py-2" placeholder="Documento o responsable" value={planFilters.text} onChange={(e) => setPlanFilters((prev) => ({ ...prev, text: e.target.value }))} />
            </label>
            <label className="block">
              <span className="mb-1 block text-sm font-medium text-gray-700">Desde</span>
              <input type="date" className="w-full rounded-lg border border-gray-300 px-3 py-2" value={planFilters.from} onChange={(e) => setPlanFilters((prev) => ({ ...prev, from: e.target.value }))} />
            </label>
            <label className="block">
              <span className="mb-1 block text-sm font-medium text-gray-700">Hasta</span>
              <input type="date" className="w-full rounded-lg border border-gray-300 px-3 py-2" value={planFilters.to} onChange={(e) => setPlanFilters((prev) => ({ ...prev, to: e.target.value }))} />
            </label>
            <div className="flex items-end">
              <button type="button" onClick={() => setPlanFilters({ text: '', from: '', to: '' })} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50">
                Limpiar filtros
              </button>
            </div>
          </FiltersCard>

          <div className="rounded-2xl border border-gray-200 bg-white shadow-sm">
            <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
              <div className="text-lg font-semibold text-gray-900">Documentos del plan estratégico</div>
              <button type="button" onClick={handleExportPlans} className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">
                <Download className="h-4 w-4" />
                Exportar
              </button>
            </div>
            <div className="p-5">
              {filteredPlans.length === 0 ? (
                <div className="text-sm text-gray-500">No hay PDFs del plan estratégico cargados.</div>
              ) : (
                <div className="space-y-3">
                  {filteredPlans.map((item) => (
                    <div key={item.id} className="flex flex-col gap-3 rounded-xl border border-gray-200 p-4 md:flex-row md:items-center md:justify-between">
                      <div>
                        <div className="font-medium text-gray-900">{item.title || 'Plan Estratégico de Seguridad Vial'}</div>
                        <div className="mt-1 text-sm text-gray-600">Fecha: {fmtDate(item.activity_date)} | Responsable: {item.responsible_name || '-'}</div>
                        {item.notes ? <div className="mt-1 text-sm text-gray-500">{item.notes}</div> : null}
                      </div>
                      <div className="flex items-center gap-2">
                        <FileLinks attachments={item.attachments} />
                        <ActionButtons onEdit={() => handleEditPlan(item)} onDelete={() => handleDeleteRecord(item.id)} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {!loading && activeTab === 'vehiculos' && (
        <div className="space-y-4">
          <form onSubmit={handleSaveVehicleDocument} className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
            <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div className="text-lg font-semibold text-gray-900">{vehicleDocForm.id ? 'Editar documento del vehículo' : 'Carpeta individual por vehículo'}</div>
              {vehicleDocForm.id ? (
                <button type="button" onClick={() => setVehicleDocForm(createVehicleDocForm())} className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50">
                  <X className="h-4 w-4" />
                  Cancelar edición
                </button>
              ) : null}
            </div>
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
              <label className="block">
                <span className="mb-1 block text-sm font-medium text-gray-700">Vehículo</span>
                <select className="w-full rounded-lg border border-gray-300 px-3 py-2" value={vehicleDocForm.related_vehicle_id} onChange={(e) => setVehicleDocForm((prev) => ({ ...prev, related_vehicle_id: e.target.value }))} required>
                  <option value="">Seleccione...</option>
                  {asArray(vehicles).map((item) => <option key={item.id} value={item.id}>{item.plate} - {item.brand} {item.model}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="mb-1 block text-sm font-medium text-gray-700">Tipo documental</span>
                <select className="w-full rounded-lg border border-gray-300 px-3 py-2" value={vehicleDocForm.document_type} onChange={(e) => setVehicleDocForm((prev) => ({ ...prev, document_type: e.target.value }))}>
                  {VEHICLE_DOCUMENT_TYPES.map((option) => <option key={option} value={option}>{option}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="mb-1 block text-sm font-medium text-gray-700">Vencimiento</span>
                <input type="date" className="w-full rounded-lg border border-gray-300 px-3 py-2" value={vehicleDocForm.due_date} onChange={(e) => setVehicleDocForm((prev) => ({ ...prev, due_date: e.target.value }))} />
              </label>
              <label className="block">
                <span className="mb-1 block text-sm font-medium text-gray-700">Estado</span>
                <input className="w-full rounded-lg border border-gray-300 px-3 py-2" value={vehicleDocForm.status} onChange={(e) => setVehicleDocForm((prev) => ({ ...prev, status: e.target.value }))} />
              </label>
              <label className="block md:col-span-2 xl:col-span-3">
                <span className="mb-1 block text-sm font-medium text-gray-700">Observaciones</span>
                <textarea rows={3} className="w-full rounded-lg border border-gray-300 px-3 py-2" value={vehicleDocForm.notes} onChange={(e) => setVehicleDocForm((prev) => ({ ...prev, notes: e.target.value }))} />
              </label>
              <label className="block">
                <span className="mb-1 block text-sm font-medium text-gray-700">Archivos</span>
                <input type="file" multiple accept=".pdf,image/*" className="w-full rounded-lg border border-gray-300 px-3 py-2" onChange={(e) => setVehicleDocForm((prev) => ({ ...prev, files: Array.from(e.target.files || []) }))} />
              </label>
            </div>
            {vehicleDocForm.id && asArray(vehicleDocForm.existingAttachments).length > 0 ? (
              <div className="mt-4">
                <div className="mb-2 text-sm font-medium text-gray-700">Archivos actuales</div>
                <FileLinks attachments={vehicleDocForm.existingAttachments} />
              </div>
            ) : null}
            <div className="mt-4">
              <button type="submit" className="inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium text-white" style={{ backgroundColor: '#F07E2D' }}>
                <Save className="h-4 w-4" />
                {vehicleDocForm.id ? 'Actualizar documento' : 'Guardar documento'}
              </button>
            </div>
          </form>

          <FiltersCard>
            <label className="block">
              <span className="mb-1 block text-sm font-medium text-gray-700">Vehículo</span>
              <select className="w-full rounded-lg border border-gray-300 px-3 py-2" value={vehicleDocFilters.vehicleId} onChange={(e) => setVehicleDocFilters((prev) => ({ ...prev, vehicleId: e.target.value }))}>
                <option value="">Todos</option>
                {asArray(vehicles).map((item) => <option key={item.id} value={item.id}>{item.plate}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="mb-1 block text-sm font-medium text-gray-700">Buscar</span>
              <input className="w-full rounded-lg border border-gray-300 px-3 py-2" placeholder="Documento, placa o estado" value={vehicleDocFilters.text} onChange={(e) => setVehicleDocFilters((prev) => ({ ...prev, text: e.target.value }))} />
            </label>
            <label className="block">
              <span className="mb-1 block text-sm font-medium text-gray-700">Vence desde</span>
              <input type="date" className="w-full rounded-lg border border-gray-300 px-3 py-2" value={vehicleDocFilters.from} onChange={(e) => setVehicleDocFilters((prev) => ({ ...prev, from: e.target.value }))} />
            </label>
            <label className="block">
              <span className="mb-1 block text-sm font-medium text-gray-700">Vence hasta</span>
              <input type="date" className="w-full rounded-lg border border-gray-300 px-3 py-2" value={vehicleDocFilters.to} onChange={(e) => setVehicleDocFilters((prev) => ({ ...prev, to: e.target.value }))} />
            </label>
          </FiltersCard>

          <div className="rounded-2xl border border-gray-200 bg-white shadow-sm">
            <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
              <div className="text-lg font-semibold text-gray-900">Documentos por vehículo</div>
              <button type="button" onClick={handleExportVehicleDocuments} className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">
                <Download className="h-4 w-4" />
                Exportar
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-left text-gray-500">
                  <tr>
                    <th className="px-4 py-3">Vehículo</th>
                    <th className="px-4 py-3">Documento</th>
                    <th className="px-4 py-3">Estado / vencimiento</th>
                    <th className="px-4 py-3">Archivos</th>
                    <th className="px-4 py-3 text-center">Acción</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredVehicleDocuments.length === 0 ? (
                    <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-500">No hay documentos registrados para el filtro actual.</td></tr>
                  ) : filteredVehicleDocuments.map((item) => {
                    const visualState = getVehicleDocumentVisualState(item);
                    return (
                      <tr key={item.id} className={`border-t align-top ${visualState.rowClass}`}>
                        <td className="px-4 py-4 text-gray-700">{item.vehicle_plate || '-'}</td>
                        <td className="px-4 py-4 text-gray-700">
                          <div className="font-medium text-gray-900">{item.category || '-'}</div>
                          {item.notes ? <div className="mt-1 text-xs text-gray-500">{item.notes}</div> : null}
                        </td>
                        <td className="px-4 py-4 text-gray-700">
                          <div className="flex flex-wrap items-center gap-2">
                            <span>{item.status || '-'}</span>
                            <span className={`inline-flex rounded-full border px-2 py-1 text-xs font-semibold ${visualState.badgeClass}`}>
                              {visualState.label}
                            </span>
                          </div>
                          <div className="text-xs text-gray-500">Vence: {fmtDate(item.due_date)}</div>
                          <div className="mt-1 text-xs font-medium text-gray-600">{visualState.helper}</div>
                        </td>
                        <td className="px-4 py-4"><FileLinks attachments={item.attachments} /></td>
                        <td className="px-4 py-4 text-center">
                          <ActionButtons onEdit={() => handleEditVehicleDocument(item)} onDelete={() => handleDeleteRecord(item.id)} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <div className="rounded-2xl border border-gray-200 bg-white shadow-sm">
            <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
              <div className="text-lg font-semibold text-gray-900">Hoja de vida de vehículos</div>
              <div className="flex flex-wrap gap-2">
                <button type="button" onClick={handleExportVehicleHistoryPdf} disabled={!selectedVehicleForSheet} className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50">
                  <FileText className="h-4 w-4" />
                  Exportar PDF
                </button>
                <button type="button" onClick={handleExportVehicleHistory} disabled={!selectedVehicleForSheet} className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50">
                  <Download className="h-4 w-4" />
                  Exportar Excel
                </button>
              </div>
            </div>
            <div className="p-5">
              {!selectedVehicleForSheet ? (
                <div className="text-sm text-gray-500">Selecciona un vehículo en la parte superior para consultar su historial de mantenimientos.</div>
              ) : (
                <div className="space-y-4">
                  <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700">
                    Vehículo seleccionado: <span className="font-semibold">{selectedVehicleForSheet.plate}</span> - {selectedVehicleForSheet.brand} {selectedVehicleForSheet.model}
                  </div>
                  <div className="flex flex-wrap gap-2 text-xs">
                    <span className="rounded-full border border-red-200 bg-red-100 px-2 py-1 font-medium text-red-700">Atrasado</span>
                    <span className="rounded-full border border-yellow-200 bg-yellow-100 px-2 py-1 font-medium text-yellow-700">Proximo / En curso</span>
                    <span className="rounded-full border border-orange-200 bg-orange-100 px-2 py-1 font-medium text-orange-700">Programado</span>
                    <span className="rounded-full border border-emerald-200 bg-emerald-100 px-2 py-1 font-medium text-emerald-700">Ejecutado</span>
                  </div>
                  <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
                    <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3">
                      <div className="text-xs uppercase tracking-wide text-gray-500">Total</div>
                      <div className="mt-1 text-2xl font-semibold text-gray-900">{vehicleHistorySummary.total}</div>
                    </div>
                    <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3">
                      <div className="text-xs uppercase tracking-wide text-red-600">Atrasados</div>
                      <div className="mt-1 text-2xl font-semibold text-red-700">{vehicleHistorySummary.atrasado}</div>
                    </div>
                    <div className="rounded-xl border border-yellow-200 bg-yellow-50 px-4 py-3">
                      <div className="text-xs uppercase tracking-wide text-yellow-700">En curso</div>
                      <div className="mt-1 text-2xl font-semibold text-yellow-700">{vehicleHistorySummary.enCurso}</div>
                    </div>
                    <div className="rounded-xl border border-orange-200 bg-orange-50 px-4 py-3">
                      <div className="text-xs uppercase tracking-wide text-orange-700">Programados</div>
                      <div className="mt-1 text-2xl font-semibold text-orange-700">{vehicleHistorySummary.programado}</div>
                    </div>
                    <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3">
                      <div className="text-xs uppercase tracking-wide text-emerald-700">Ejecutados</div>
                      <div className="mt-1 text-2xl font-semibold text-emerald-700">{vehicleHistorySummary.ejecutado}</div>
                    </div>
                  </div>
                  <FiltersCard>
                    <label className="block">
                      <span className="mb-1 block text-sm font-medium text-gray-700">Tipo</span>
                      <select className="w-full rounded-lg border border-gray-300 px-3 py-2" value={vehicleHistoryFilters.type} onChange={(e) => setVehicleHistoryFilters((prev) => ({ ...prev, type: e.target.value }))}>
                        <option value="">Todos</option>
                        {vehicleHistoryTypeOptions.map((option) => <option key={option} value={option}>{option}</option>)}
                      </select>
                    </label>
                    <label className="block">
                      <span className="mb-1 block text-sm font-medium text-gray-700">Estado</span>
                      <select className="w-full rounded-lg border border-gray-300 px-3 py-2" value={vehicleHistoryFilters.state} onChange={(e) => setVehicleHistoryFilters((prev) => ({ ...prev, state: e.target.value }))}>
                        <option value="">Todos</option>
                        {['Proximo', 'Programado', 'En curso', 'Atrasado', 'Ejecutado', 'Historico'].map((option) => (
                          <option key={option} value={option}>{option}</option>
                        ))}
                      </select>
                    </label>
                    <label className="block">
                      <span className="mb-1 block text-sm font-medium text-gray-700">Desde</span>
                      <input type="date" className="w-full rounded-lg border border-gray-300 px-3 py-2" value={vehicleHistoryFilters.from} onChange={(e) => setVehicleHistoryFilters((prev) => ({ ...prev, from: e.target.value }))} />
                    </label>
                    <label className="block">
                      <span className="mb-1 block text-sm font-medium text-gray-700">Hasta</span>
                      <input type="date" className="w-full rounded-lg border border-gray-300 px-3 py-2" value={vehicleHistoryFilters.to} onChange={(e) => setVehicleHistoryFilters((prev) => ({ ...prev, to: e.target.value }))} />
                    </label>
                  </FiltersCard>
                  <div className="flex justify-end">
                    <button type="button" onClick={() => setVehicleHistoryFilters({ type: '', state: '', from: '', to: '' })} className="rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50">
                      Limpiar filtros
                    </button>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead className="bg-gray-50 text-left text-gray-500">
                        <tr>
                          <th className="px-4 py-3">Fecha</th>
                          <th className="px-4 py-3">Tipo</th>
                          <th className="px-4 py-3">Descripción</th>
                          <th className="px-4 py-3">Kilometraje</th>
                          <th className="px-4 py-3">Costo</th>
                          <th className="px-4 py-3">Estado</th>
                          <th className="px-4 py-3">Soportes</th>
                        </tr>
                      </thead>
                      <tbody>
                        {loadingVehicleHistory ? (
                          <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-500">Cargando historial...</td></tr>
                        ) : filteredVehicleHistory.length === 0 ? (
                          <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-500">No hay mantenimientos para los filtros seleccionados.</td></tr>
                        ) : filteredVehicleHistory.map((item) => {
                          const visualState = getMaintenanceHistoryVisualState(item);
                          return (
                          <tr key={item.id} className={`border-t align-top ${visualState.rowClass}`}>
                            <td className="px-4 py-4 text-gray-700">{fmtDate(item.start_date || item.date)}</td>
                            <td className="px-4 py-4 text-gray-700">{item.type || '-'}</td>
                            <td className="px-4 py-4 text-gray-700">{item.description || '-'}</td>
                            <td className="px-4 py-4 text-gray-700">{item.mileage || '-'}</td>
                            <td className="px-4 py-4 text-gray-700">{fmtMoney(item.cost)}</td>
                            <td className="px-4 py-4 text-gray-700">
                              <div className={`inline-flex rounded-full border px-2 py-1 text-xs font-semibold ${visualState.badgeClass}`}>
                                {visualState.label}
                              </div>
                              <div className="mt-1 text-xs text-gray-500">{visualState.helper}</div>
                              {item.end_date ? <div className="mt-1 text-xs text-gray-500">Cierre: {fmtDate(item.end_date)}</div> : null}
                            </td>
                            <td className="px-4 py-4">
                              <FileLinks attachments={asArray(item.support_files).length ? asArray(item.support_files).map((url) => ({ name: String(url).split('/').pop(), url })) : (item.support_file ? [{ name: String(item.support_file).split('/').pop(), url: item.support_file }] : [])} />
                            </td>
                          </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {!loading && activeTab === 'infracciones' && (
        <div className="space-y-4">
          <form onSubmit={handleSaveInfraction} className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
            <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div className="text-lg font-semibold text-gray-900">{infractionForm.id ? 'Editar infracción' : 'Registrar infracción'}</div>
              {infractionForm.id ? (
                <button type="button" onClick={() => setInfractionForm(createInfractionForm())} className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50">
                  <X className="h-4 w-4" />
                  Cancelar edición
                </button>
              ) : null}
            </div>
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
              <label className="block">
                <span className="mb-1 block text-sm font-medium text-gray-700">Conductor</span>
                <select className="w-full rounded-lg border border-gray-300 px-3 py-2" value={infractionForm.related_driver_id} onChange={(e) => setInfractionForm((prev) => ({ ...prev, related_driver_id: e.target.value }))} required>
                  <option value="">Seleccione...</option>
                  {asArray(drivers).map((item) => <option key={item.id} value={item.id}>{item.nombre} - {item.cedula}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="mb-1 block text-sm font-medium text-gray-700">Vehículo</span>
                <select className="w-full rounded-lg border border-gray-300 px-3 py-2" value={infractionForm.related_vehicle_id} onChange={(e) => setInfractionForm((prev) => ({ ...prev, related_vehicle_id: e.target.value }))} required>
                  <option value="">Seleccione...</option>
                  {asArray(vehicles).map((item) => <option key={item.id} value={item.id}>{item.plate} - {item.brand} {item.model}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="mb-1 block text-sm font-medium text-gray-700">Infracción</span>
                <input className="mb-2 w-full rounded-lg border border-gray-300 px-3 py-2" placeholder="Buscar por código o nombre" value={infractionSearch} onChange={(e) => setInfractionSearch(e.target.value)} />
                <select className="w-full rounded-lg border border-gray-300 px-3 py-2" value={infractionForm.infraction_name} onChange={(e) => handleInfractionCatalogChange(e.target.value)} required>
                  <option value="">Seleccione...</option>
                  {filteredInfractionCatalog.map((item) => <option key={item.id} value={item.name}>{item.code ? `${item.code} - ` : ''}{item.name}</option>)}
                </select>
                <span className="mt-1 block text-xs text-gray-500">{filteredInfractionCatalog.length} opciones disponibles</span>
              </label>
              <label className="block">
                <span className="mb-1 block text-sm font-medium text-gray-700">Fecha</span>
                <input type="date" className="w-full rounded-lg border border-gray-300 px-3 py-2" value={infractionForm.activity_date} onChange={(e) => setInfractionForm((prev) => ({ ...prev, activity_date: e.target.value }))} />
              </label>
              <label className="block">
                <span className="mb-1 block text-sm font-medium text-gray-700">Estado</span>
                <select className="w-full rounded-lg border border-gray-300 px-3 py-2" value={infractionForm.status} onChange={(e) => setInfractionForm((prev) => ({ ...prev, status: e.target.value }))}>
                  {INFRACTION_STATUS_OPTIONS.map((option) => <option key={option} value={option}>{option}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="mb-1 block text-sm font-medium text-gray-700">Autoridad / comparendo</span>
                <input className="w-full rounded-lg border border-gray-300 px-3 py-2" value={infractionForm.authority} onChange={(e) => setInfractionForm((prev) => ({ ...prev, authority: e.target.value }))} />
              </label>
              <label className="block">
                <span className="mb-1 block text-sm font-medium text-gray-700">Valor</span>
                <input className="w-full rounded-lg border border-gray-300 px-3 py-2" value={infractionForm.amount} onChange={(e) => setInfractionForm((prev) => ({ ...prev, amount: e.target.value }))} />
              </label>
              <label className="block">
                <span className="mb-1 block text-sm font-medium text-gray-700">Número de comparendo</span>
                <input className="w-full rounded-lg border border-gray-300 px-3 py-2" value={infractionForm.ticket_number} onChange={(e) => setInfractionForm((prev) => ({ ...prev, ticket_number: e.target.value }))} />
              </label>
              <label className="block md:col-span-2 xl:col-span-3">
                <span className="mb-1 block text-sm font-medium text-gray-700">Lugar / observaciones</span>
                <textarea rows={3} className="w-full rounded-lg border border-gray-300 px-3 py-2" value={`${infractionForm.location}${infractionForm.notes ? `\n${infractionForm.notes}` : ''}`} onChange={(e) => {
                  const lines = e.target.value.split('\n');
                  setInfractionForm((prev) => ({
                    ...prev,
                    location: lines[0] || '',
                    notes: lines.slice(1).join('\n'),
                  }));
                }} />
              </label>
              <label className="block">
                <span className="mb-1 block text-sm font-medium text-gray-700">Evidencias</span>
                <input type="file" multiple accept=".pdf,image/*" className="w-full rounded-lg border border-gray-300 px-3 py-2" onChange={(e) => setInfractionForm((prev) => ({ ...prev, files: Array.from(e.target.files || []) }))} />
              </label>
            </div>
            {infractionForm.id && asArray(infractionForm.existingAttachments).length > 0 ? (
              <div className="mt-4">
                <div className="mb-2 text-sm font-medium text-gray-700">Evidencias actuales</div>
                <FileLinks attachments={infractionForm.existingAttachments} />
              </div>
            ) : null}
            <div className="mt-4">
              <button type="submit" className="inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium text-white" style={{ backgroundColor: '#F07E2D' }}>
                <Save className="h-4 w-4" />
                {infractionForm.id ? 'Actualizar infracción' : 'Guardar infracción'}
              </button>
            </div>
          </form>

          <FiltersCard>
            <label className="block">
              <span className="mb-1 block text-sm font-medium text-gray-700">Conductor</span>
              <select className="w-full rounded-lg border border-gray-300 px-3 py-2" value={infractionFilters.driverId} onChange={(e) => setInfractionFilters((prev) => ({ ...prev, driverId: e.target.value }))}>
                <option value="">Todos</option>
                {asArray(drivers).map((item) => <option key={item.id} value={item.id}>{item.nombre}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="mb-1 block text-sm font-medium text-gray-700">Vehículo</span>
              <select className="w-full rounded-lg border border-gray-300 px-3 py-2" value={infractionFilters.vehicleId} onChange={(e) => setInfractionFilters((prev) => ({ ...prev, vehicleId: e.target.value }))}>
                <option value="">Todos</option>
                {asArray(vehicles).map((item) => <option key={item.id} value={item.id}>{item.plate}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="mb-1 block text-sm font-medium text-gray-700">Buscar</span>
              <input className="w-full rounded-lg border border-gray-300 px-3 py-2" placeholder="Código, infracción o comparendo" value={infractionFilters.text} onChange={(e) => setInfractionFilters((prev) => ({ ...prev, text: e.target.value }))} />
            </label>
            <label className="block">
              <span className="mb-1 block text-sm font-medium text-gray-700">Desde</span>
              <input type="date" className="w-full rounded-lg border border-gray-300 px-3 py-2" value={infractionFilters.from} onChange={(e) => setInfractionFilters((prev) => ({ ...prev, from: e.target.value }))} />
            </label>
            <label className="block">
              <span className="mb-1 block text-sm font-medium text-gray-700">Hasta</span>
              <input type="date" className="w-full rounded-lg border border-gray-300 px-3 py-2" value={infractionFilters.to} onChange={(e) => setInfractionFilters((prev) => ({ ...prev, to: e.target.value }))} />
            </label>
            <div className="flex items-end">
              <button type="button" onClick={() => setInfractionFilters({ vehicleId: '', driverId: '', text: '', from: '', to: '' })} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50">
                Limpiar filtros
              </button>
            </div>
          </FiltersCard>

          <div className="rounded-2xl border border-gray-200 bg-white shadow-sm">
            <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
              <div className="text-lg font-semibold text-gray-900">Listado general de infracciones</div>
              <button type="button" onClick={handleExportInfractions} className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">
                <Download className="h-4 w-4" />
                Exportar
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-left text-gray-500">
                  <tr>
                    <th className="px-4 py-3">Conductor</th>
                    <th className="px-4 py-3">Vehículo</th>
                    <th className="px-4 py-3">Infracción</th>
                    <th className="px-4 py-3">Fecha / estado</th>
                    <th className="px-4 py-3">Valor</th>
                    <th className="px-4 py-3">Evidencias</th>
                    <th className="px-4 py-3 text-center">Acción</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredInfractions.length === 0 ? (
                    <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-500">No hay infracciones registradas.</td></tr>
                  ) : filteredInfractions.map((item) => (
                    <tr key={item.id} className="border-t align-top">
                      <td className="px-4 py-4 text-gray-700">
                        <div className="font-medium text-gray-900">{item.driver_name || '-'}</div>
                        <div className="text-xs text-gray-500">{item.driver_doc || '-'}</div>
                      </td>
                      <td className="px-4 py-4 text-gray-700">{item.vehicle_plate || '-'}</td>
                      <td className="px-4 py-4 text-gray-700">
                        <div className="font-medium text-gray-900">{item.category || '-'}</div>
                        <div className="text-xs text-gray-500">{item.code || '-'}</div>
                        {item.payload?.ticket_number ? <div className="text-xs text-gray-500">Comparendo: {item.payload.ticket_number}</div> : null}
                      </td>
                      <td className="px-4 py-4 text-gray-700">
                        <div>{fmtDate(item.activity_date)}</div>
                        <div className="text-xs text-gray-500">{item.status || '-'}</div>
                      </td>
                      <td className="px-4 py-4 text-gray-700">{fmtMoney(item.payload?.amount)}</td>
                      <td className="px-4 py-4"><FileLinks attachments={item.attachments} /></td>
                      <td className="px-4 py-4 text-center">
                        <ActionButtons onEdit={() => handleEditInfraction(item)} onDelete={() => handleDeleteRecord(item.id)} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {!loading && activeTab === 'autogestion' && (
        <div className="space-y-4">
          <form onSubmit={handleSaveAutogestion} className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
            <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div className="text-lg font-semibold text-gray-900">{autogestionForm.id ? 'Editar reporte de autogestión' : 'Reporte de autogestión del PESV'}</div>
              {autogestionForm.id ? (
                <button type="button" onClick={() => setAutogestionForm(createAutogestionForm())} className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50">
                  <X className="h-4 w-4" />
                  Cancelar edición
                </button>
              ) : null}
            </div>
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
              <label className="block xl:col-span-2">
                <span className="mb-1 block text-sm font-medium text-gray-700">Título</span>
                <input className="w-full rounded-lg border border-gray-300 px-3 py-2" value={autogestionForm.title} onChange={(e) => setAutogestionForm((prev) => ({ ...prev, title: e.target.value }))} />
              </label>
              <label className="block">
                <span className="mb-1 block text-sm font-medium text-gray-700">Fecha</span>
                <input type="date" className="w-full rounded-lg border border-gray-300 px-3 py-2" value={autogestionForm.activity_date} onChange={(e) => setAutogestionForm((prev) => ({ ...prev, activity_date: e.target.value }))} />
              </label>
              <label className="block">
                <span className="mb-1 block text-sm font-medium text-gray-700">Estado</span>
                <select className="w-full rounded-lg border border-gray-300 px-3 py-2" value={autogestionForm.status} onChange={(e) => setAutogestionForm((prev) => ({ ...prev, status: e.target.value }))}>
                  {REPORT_STATUS_OPTIONS.map((option) => <option key={option} value={option}>{option}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="mb-1 block text-sm font-medium text-gray-700">Año</span>
                <input className="w-full rounded-lg border border-gray-300 px-3 py-2" value={autogestionForm.period_year} onChange={(e) => setAutogestionForm((prev) => ({ ...prev, period_year: e.target.value }))} />
              </label>
              <label className="block">
                <span className="mb-1 block text-sm font-medium text-gray-700">Periodo</span>
                <input className="w-full rounded-lg border border-gray-300 px-3 py-2" value={autogestionForm.period_label} onChange={(e) => setAutogestionForm((prev) => ({ ...prev, period_label: e.target.value }))} />
              </label>
              <label className="block xl:col-span-2">
                <span className="mb-1 block text-sm font-medium text-gray-700">Responsable</span>
                <input className="w-full rounded-lg border border-gray-300 px-3 py-2" value={autogestionForm.responsible_name} onChange={(e) => setAutogestionForm((prev) => ({ ...prev, responsible_name: e.target.value }))} />
              </label>
              <label className="block md:col-span-2 xl:col-span-3">
                <span className="mb-1 block text-sm font-medium text-gray-700">Observaciones</span>
                <textarea rows={3} className="w-full rounded-lg border border-gray-300 px-3 py-2" value={autogestionForm.notes} onChange={(e) => setAutogestionForm((prev) => ({ ...prev, notes: e.target.value }))} />
              </label>
              <label className="block">
                <span className="mb-1 block text-sm font-medium text-gray-700">Evidencias</span>
                <input type="file" multiple accept=".pdf,image/*" className="w-full rounded-lg border border-gray-300 px-3 py-2" onChange={(e) => setAutogestionForm((prev) => ({ ...prev, files: Array.from(e.target.files || []) }))} />
              </label>
            </div>
            {autogestionForm.id && asArray(autogestionForm.existingAttachments).length > 0 ? (
              <div className="mt-4">
                <div className="mb-2 text-sm font-medium text-gray-700">Soportes actuales</div>
                <FileLinks attachments={autogestionForm.existingAttachments} />
              </div>
            ) : null}
            <div className="mt-4">
              <button type="submit" className="inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium text-white" style={{ backgroundColor: '#F07E2D' }}>
                <Save className="h-4 w-4" />
                {autogestionForm.id ? 'Actualizar reporte' : 'Guardar reporte'}
              </button>
            </div>
          </form>

          <FiltersCard>
            <label className="block">
              <span className="mb-1 block text-sm font-medium text-gray-700">Buscar</span>
              <input className="w-full rounded-lg border border-gray-300 px-3 py-2" placeholder="Reporte, responsable o periodo" value={autogestionFilters.text} onChange={(e) => setAutogestionFilters((prev) => ({ ...prev, text: e.target.value }))} />
            </label>
            <label className="block">
              <span className="mb-1 block text-sm font-medium text-gray-700">Desde</span>
              <input type="date" className="w-full rounded-lg border border-gray-300 px-3 py-2" value={autogestionFilters.from} onChange={(e) => setAutogestionFilters((prev) => ({ ...prev, from: e.target.value }))} />
            </label>
            <label className="block">
              <span className="mb-1 block text-sm font-medium text-gray-700">Hasta</span>
              <input type="date" className="w-full rounded-lg border border-gray-300 px-3 py-2" value={autogestionFilters.to} onChange={(e) => setAutogestionFilters((prev) => ({ ...prev, to: e.target.value }))} />
            </label>
            <div className="flex items-end">
              <button type="button" onClick={() => setAutogestionFilters({ text: '', from: '', to: '' })} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50">
                Limpiar filtros
              </button>
            </div>
          </FiltersCard>

          <div className="rounded-2xl border border-gray-200 bg-white shadow-sm">
            <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
              <div className="text-lg font-semibold text-gray-900">Reportes cargados</div>
              <button type="button" onClick={handleExportAutogestion} className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">
                <Download className="h-4 w-4" />
                Exportar
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-left text-gray-500">
                  <tr>
                    <th className="px-4 py-3">Reporte</th>
                    <th className="px-4 py-3">Periodo</th>
                    <th className="px-4 py-3">Fecha</th>
                    <th className="px-4 py-3">Estado</th>
                    <th className="px-4 py-3">Responsable</th>
                    <th className="px-4 py-3">Soportes</th>
                    <th className="px-4 py-3 text-center">Acción</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAutogestion.length === 0 ? (
                    <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-500">No hay reportes de autogestión registrados.</td></tr>
                  ) : filteredAutogestion.map((item) => (
                    <tr key={item.id} className="border-t align-top">
                      <td className="px-4 py-4 text-gray-700">
                        <div className="font-medium text-gray-900">{item.title || '-'}</div>
                        {item.notes ? <div className="mt-1 text-xs text-gray-500">{item.notes}</div> : null}
                      </td>
                      <td className="px-4 py-4 text-gray-700">{item.period_label || '-'} / {item.period_year || '-'}</td>
                      <td className="px-4 py-4 text-gray-700">{fmtDate(item.activity_date)}</td>
                      <td className="px-4 py-4 text-gray-700">{item.status || '-'}</td>
                      <td className="px-4 py-4 text-gray-700">{item.responsible_name || '-'}</td>
                      <td className="px-4 py-4"><FileLinks attachments={item.attachments} /></td>
                      <td className="px-4 py-4 text-center">
                        <ActionButtons onEdit={() => handleEditAutogestion(item)} onDelete={() => handleDeleteRecord(item.id)} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
